import { getState, setState, subscribe, type EncryptedHint } from '../store';
import { advanceLocationIndex, reconcileLocationIndex } from '../store-actions';
import { navigate } from '../router';
import { verifyGeoAnswer, submitAnswer } from '../api/game-api';
import { searchCities, searchStreets, searchBuildings, type AddressSuggestion } from '../data/address-service';
import { createTimer } from '../components/timer';
import { vibrateWrongAnswer } from '../utils/haptic';
import { verifyGeoLocal } from '../utils/geo-verify-local';
import { verifyBcrypt } from '../crypto/bcrypt-verify';
import { keyFromBase64, deriveKey, decrypt } from '../crypto/crypto-utils';
import { createGameMenu } from '../components/game-menu';
import { onTeamEvent } from '../api/team-sync';
import { handleAdvanceEvent, handleCaughtUpEvent, handleGeoVerifiedEvent, handlePenaltyEvent } from '../components/team-event-toast';
import { tryAutoSelect, nextIndex } from '../utils/fias-autoselect';
// SOS lives inside createGameMenu (session 47e) — no header button here.
import { buildYandexMapsUrl } from '../utils/yandex-maps-url';
import { triggerAdvance } from '../utils/auto-advance';
import { escapeHtml } from '../utils/escape';
import { getElapsedSec, anchorFromServer } from '../utils/time-utils';
import { wireClickToCopy } from '../utils/clipboard';
import { pollPlayerStateOnce } from '../utils/poll-player-state';
import { capturePlayerPosition } from '../utils/player-position';
import { fetchPlayerState } from '../utils/auth-failure';
import { ReconcileGate } from '../utils/reconcile-gate';
import { createViewLifecycle, type ViewLifecycle } from '../utils/view-lifecycle';
import { installVisibilityReconcile } from '../utils/visibility-reconciler';
// /investigate 2026-05-04 (game 773697 demo run) — restored. Removed in 50a8a5d
// «Demo autopilot removed» 2026-04-20 without a replacement, leaving demo runs
// effectively unplayable: 50× speed multiplier still applied, but no bot to
// click through stages → human couldn't keep up, timer expired in seconds,
// stage never advanced. demo-bot.ts module survived as dead code; re-wiring
// it back into the phase dispatcher restores the «watch mode» preview.
import { scheduleDemoBotAction, cancelDemoBot } from '../utils/demo-bot';

type Phase = 'riddle' | 'input' | 'site-code';

// Module-level handles for cleanup on view unmount.
//
// Plan B Task 8 (2026-05-11) — visibilityHandler / pageshowHandler /
// orgAdvancePoll are now owned by `activeLc`. The poll interval is registered
// via activeLc.setInterval; the visibility/pageshow listeners install via the
// visibility-reconciler foundation (see Step 3 of renderLocationView). All
// three resources tear down through activeLc.cleanup() in destroy.
//
// hintTimer + progressTimer stay module-scoped because phase transitions
// (renderRiddlePhase / renderInputPhase / renderSiteCodePhase) need to clear
// JUST the prior timer in the `clear → create` pattern, and ViewLifecycle
// has no per-handle cancel API. Their destroy-side teardown lives in an
// activeLc.onDestroy closure that reads the LATEST module-level reference
// at cleanup time (so a phase transition right before destroy doesn't leave
// a stale pre-transition handle ticking).
//
// timerHandle / menuHandle / teamEventUnsubscribe / copyDisposers /
// chatBadgeUnsubscribe stay module-scoped for the same reason — they're
// mutated mid-render across phase transitions; lc.adopt would freeze the
// wrong reference. Their cleanup fires via lc.onDestroy closures that read
// the latest value at teardown.
let timerHandle: { destroy: () => void } | null = null;
let menuHandle: { destroy: () => void } | null = null;
let chatBadgeUnsubscribe: (() => void) | null = null;
let hintTimer: ReturnType<typeof setInterval> | null = null;
let progressTimer: ReturnType<typeof setInterval> | null = null;
let copyDisposers: Array<() => void> = [];
let activeLc: ViewLifecycle | null = null;

function releaseChatBadgeSubscription(): void {
  if (chatBadgeUnsubscribe) {
    chatBadgeUnsubscribe();
    chatBadgeUnsubscribe = null;
  }
}

/** Wire up a chat button: click -> navigate('chat'), live badge for unread count. */
function wireChatButton(container: HTMLElement, buttonId: string, badgeId: string): void {
  const btn = container.querySelector<HTMLElement>('#' + buttonId);
  const badge = container.querySelector<HTMLElement>('#' + badgeId);
  if (!btn || !badge) return;
  btn.addEventListener('click', () => navigate('chat'));
  releaseChatBadgeSubscription();
  chatBadgeUnsubscribe = subscribe((s) => {
    if (s.unreadChatCount > 0) {
      badge.textContent = String(s.unreadChatCount);
      badge.style.display = 'inline-flex';
    } else {
      badge.style.display = 'none';
    }
  });
}

/** Render one media attachment (image/video/audio). Kept separate so
 *  `renderMedia` below can stack multiple items.
 *
 *  Exported for unit testing — the regression that made media invisible
 *  on /riddle (session 47e) lived in the wiring between this function
 *  and the phase that actually renders, so it needs isolated tests. */
export function renderMediaItem(url: string, type: string, hasTextClue?: boolean): string {
  const imageAlt = hasTextClue
    ? ''
    : 'Визуальная загадка. Если не видите изображение, запросите подсказку у организатора.';
  const videoAria = hasTextClue
    ? 'Видео-часть загадки (текст — ниже)'
    : 'Видео-загадка. Если не видите видео, запросите подсказку у организатора.';
  const audioAria = hasTextClue
    ? 'Аудио-часть загадки (текст — ниже)'
    : 'Аудио-загадка. Прослушайте запись.';
  // URL comes from CreateGameRequest.additionalMedia[].url or .mediaUrl —
  // organizer-controlled input through the dashboard. A raw interpolation
  // here breaks out of src="" on `"` in the URL and runs attacker JS on
  // every player who joins the quest. escapeHtml neutralizes it.
  const safeUrl = escapeHtml(url);
  switch (type) {
    case 'IMAGE':
      return `<div style="margin-bottom: var(--sp-md); text-align: center;">
        <img src="${safeUrl}" alt="${imageAlt}" style="max-width: 100%; max-height: 300px; border-radius: var(--radius-md); object-fit: contain;" />
      </div>`;
    case 'VIDEO':
      return `<div style="margin-bottom: var(--sp-md); text-align: center;">
        <video src="${safeUrl}" controls playsinline aria-label="${videoAria}" style="max-width: 100%; max-height: 300px; border-radius: var(--radius-md);"></video>
      </div>`;
    case 'AUDIO':
      return `<div style="margin-bottom: var(--sp-md);">
        <audio src="${safeUrl}" controls aria-label="${audioAria}" style="width: 100%;"></audio>
      </div>`;
    default:
      return '';
  }
}

/** Render primary media + any additional attachments in DOM order.
 *
 *  Exported so unit tests can assert stacking + empty-list + invalid-entry
 *  filtering behavior without spinning up the entire location-view. */
export function renderMedia(
  url?: string,
  type?: string,
  hasTextClue?: boolean,
  additional?: Array<{ url: string; type: string }>,
): string {
  const primary = url ? renderMediaItem(url, type || '', hasTextClue) : '';
  const extras = (additional ?? [])
    .filter(m => m.url && m.type)
    .map(m => renderMediaItem(m.url, m.type, hasTextClue))
    .join('');
  return primary + extras;
}

// Session 50 §5d.bis — unsubscribe from team-event broadcasts when the
// view is torn down so the next mount gets a fresh handler registration.
let teamEventUnsubscribe: (() => void) | null = null;

// Plan B Task 8 (2026-05-11) — visibilityHandler / pageshowHandler module-
// level lets removed. The visibility-reconciler installer wires both
// listeners via activeLc.addListener so `installVisibilityReconcile` owns
// the cleanup. The /investigate 2026-04-27 iPhone Я.Карты + v2 history
// (cold-mount reconcile only catches «browser killed the tab» path; iOS
// Safari «suspended → resumed from frozen state» needs visibilitychange;
// session-restore + bfcache need pageshow.persisted) is preserved
// architecturally — the installer fires the same recovery body for both
// resume paths, and the route guard + persisted filter live INSIDE the
// installer where call sites can't drop them.
//
// Reconcile gate — owns concurrent-collapse lock + 15s abort timeout for the
// post-mount handlers (visibility, pageshow). Cold-mount renderLocationView
// calls `reconcileFromServer` directly via `gate.runWithoutLock` (single-shot,
// doesn't race with itself). Visibility/pageshow handlers wrap the same body
// via `gate.run` to serialize. iOS Safari fires both events nearly
// simultaneously on resume; without the lock, two parallel fetches both
// resolve `r.changed=true` → two parallel `destroyLocationView() +
// renderLocationView()` → second render's hintTimer overwrites the first's
// ref while the first keeps ticking → 1Hz flicker (v0.79.14 bug class).
const locationGate = new ReconcileGate();
const NO_CHANGE = { changed: false, finished: false };

/**
 * Lock-gated wrapper used by visibility + pageshow handlers. Mirrors the
 * pattern in lobby-view / chat-view / spectator-view. Returns NO_CHANGE on
 * concurrent invocation (collapses iOS Safari's near-simultaneous
 * visibilitychange + pageshow firing on resume).
 *
 * Strips `authFailed` from the public return type — visibility handlers don't
 * consume it (auth-failure is observed via the global `aq:auth-permanent-failure`
 * event handler in main.ts).
 */
export async function gatedReconcileFromServer(): Promise<{ changed: boolean; finished: boolean }> {
  const result = await locationGate.run(NO_CHANGE, NO_CHANGE, reconcileBody);
  return { changed: result.changed, finished: result.finished };
}

// Per-location input drafts so a round-trip to chat (or any other view)
// doesn't wipe what the player already typed. Keyed by location index.
// Cleared on a successful advance in doSubmit. Module-local: survives
// navigate('chat') + navigate('location') within a session, does NOT
// persist across F5 (and that's fine — F5 is rare enough that asking
// the player to re-type is acceptable).
type LocationDraft = {
  lat?: string;
  lon?: string;
  city?: string;
  street?: string;
  house?: string;
  siteCodes?: string[];
};
const locationDrafts = new Map<number, LocationDraft>();

function getDraft(idx: number): LocationDraft {
  let d = locationDrafts.get(idx);
  if (!d) { d = {}; locationDrafts.set(idx, d); }
  return d;
}

function clearDraft(idx: number): void {
  locationDrafts.delete(idx);
}

export function destroyLocationView(): void {
  // Plan B Task 8 (2026-05-11) — orgAdvancePoll + progressTimer + hintTimer +
  // timerHandle.destroy + menuHandle.destroy + copyDisposers drain +
  // releaseChatBadgeSubscription + teamEventUnsubscribe + visibility/pageshow
  // listeners are released via `activeLc.cleanup()`. The genuinely module-
  // scoped bits (ws, locationGate, demo-bot timer, team-event toasts,
  // auto-advance overlay) stay in the explicit destroy below because they're
  // referenced by closures or lifecycles outside the lc-managed scope (gate
  // is shared across renders for inflight-recovery; demo-bot lives in its own
  // module; toasts + overlay are body-attached singletons that migrate to
  // bodyOverlays in Tasks 9-10).
  activeLc?.cleanup();
  activeLc = null;

  // Drop any pending demo-bot fire — the user navigated away from the
  // location view (chat / results / etc.). Idempotent, no-op when the bot
  // wasn't scheduled (real-player join). Note: location-view does NOT own
  // its own WebSocket; the team WS lives in `pwa/src/api/team-sync.ts` as
  // module-level `currentWs` and team-sync owns its own visibility/pageshow
  // handlers + forceReconnect calls (out-of-scope per Task 8 design memo).
  cancelDemoBot();
  // /review 2026-05-02 — drop the inflight lock if still held. A
  // gatedReconcileFromServer in flight during destroy will clear the flag
  // in its own finally, but that finally runs AFTER the next mount's first
  // visibility/pageshow flip, causing it to bail with 'inflight'. Match
  // the pattern in destroyChatView and destroySpectatorView.
  locationGate.release();
  // Plan B Task 10 (2026-05-11) — team-event-toast sweep is now wired through
  // `lc.onDestroy(dismiss)` per toast (registered inside `showToastWithLc`).
  // Every view-scoped toast variant (handleAdvanceEvent / handleCaughtUpEvent
  // / handlePenaltyEvent / handleGeoVerifiedEvent) routes through that
  // wrapper, so `activeLc?.cleanup()` above clears them all — mid-typing CTAs
  // (no auto-dismiss) and pending auto-dismiss callbacks (e.g. CAUGHT_UP
  // onCompleteSync → goTo) included. Pre-extraction this was
  // `dismissAllTeamToasts()` (a global sweep). See showToastWithLc KDoc for
  // why the bodyOverlays primitive wasn't used here (would have broken
  // stacking semantics on the same-view second-show case).
  //
  // Plan B Task 9 (2026-05-11) — auto-advance full-screen overlay lives in
  // bodyOverlays. Cleanup registered via `lc.onDestroy` runs through
  // `activeLc?.cleanup()` above, so no explicit sweep is needed here either.
  // Pre-extraction this was `clearAutoAdvanceOverlay()`.
}

/**
 * Shared reconcile body. Used by both `reconcileFromServer` (cold-mount,
 * lock-free) and `gatedReconcileFromServer` (visibility/pageshow, lock-gated).
 * The signal is provided by the surrounding `gate.run` / `gate.runWithoutLock`
 * call — the body just threads it into the fetch.
 *
 * Session 89 — reconcile local state against the server's authoritative
 * Teams.currentLocationIndex on every cold mount.
 *
 * Bug closed: capt A submits a correct answer → server advances team →
 * TeamEvent.ADVANCE broadcast fires through the team WS channel. Capt B
 * was offline (browser closed) — that frame went into the void. When B
 * reopens, restorePersistedState rehydrates state.currentLocationIndex
 * from localStorage (stale), location-view mounts on the stale index,
 * decrypts the stale riddle. WS reconnects but events are push-only — no
 * replay of the missed ADVANCE. B sits on the old riddle forever.
 *
 * The pre-existing recovery branch at the bottom of renderLocationView
 * fires only when `nextKeys[currentLocationIndex - 1]` is missing. In
 * the bug scenario, B's prevKey is present (it was valid for stage 1
 * when B last saved), so that branch never fires.
 *
 * Fix: at the top of renderLocationView, fetch /player-state and
 * reconcile. If server says we're at a different index OR the team has
 * finished, update state and re-render (or navigate to results). If
 * server agrees, no-op.
 *
 * Skipped for fullOffline games — PWA owns the timer there, server is mostly
 * inert for gameplay; reconcile would downgrade the client.
 *
 * Cost: one HTTP roundtrip on every mount. Mounts happen <10× per
 * quest typically. Worth the correctness gain.
 */
async function reconcileBody(signal: AbortSignal): Promise<{ changed: boolean; finished: boolean; authFailed?: boolean }> {
  const state = getState();
  if (state.fullOffline) return NO_CHANGE;
  if (!state.gameCode || !state.playerId || !state.playerToken) {
    return NO_CHANGE;
  }

  const result = await fetchPlayerState({
    gameCode: state.gameCode,
    playerId: state.playerId,
    playerToken: state.playerToken,
    context: 'reconcile-location',
    signal,
  });
  if (result.kind === 'auth-failed') {
    return { ...NO_CHANGE, authFailed: true };
  }
  if (result.kind === 'network-blip') return NO_CHANGE;
  const ps = result.data;
  // Server says the team has already finished — let the normal completion
  // path take over (keeps totalTimeMs + completedAt consistent with the
  // leaderboard).
  if (ps.finishedAt) {
    advanceLocationIndex(ps.currentLocationIndex, {
      completedAt: ps.finishedAt,
      status: 'FINISHED',
    });
    return { changed: false, finished: true };
  }
  const serverIdx = ps.currentLocationIndex;
  if (typeof serverIdx !== 'number') return NO_CHANGE;
  // /investigate 2026-04-27 prod game 265276 — re-anchor `locationStartedAt`
  // unconditionally from server's `locationEnteredAt + serverNow`. Pre-fix
  // both branches below skipped the anchor, so a client that came back
  // online after a server-side TIMEOUT advance kept the OLD anchor (often
  // gameStartedAt or a previous-stage value), the timer ran 6+ min behind
  // server truth, triggerAdvance fired once on mount, server rejected as
  // not-yet-expired, and the player sat on an «overdue» riddle forever.
  // Anchor sync must run BEFORE the same-index fast-path returns.
  const reanchored = anchorFromServer(ps.locationEnteredAt, ps.serverNow);
  const anchorDriftMs = Math.abs(reanchored - state.locationStartedAt);
  // /investigate 2026-04-27 prod game 734032 — server-authoritative
  // geo-verified state for the current stage. Build from server response.
  //
  // Follow-up 2026-04-27 evening — ADDRESS-type stages without geocoded
  // target coords come back with `geoVerifiedLat=null, geoVerifiedLon=null,
  // geoVerifiedAddress="..."`. Pre-fix the strict `lat!=null && lon!=null`
  // gate dropped these on the floor → Capt B reopening saw riddle phase
  // even though the team had verified by address. Address alone is a
  // sufficient verification signal; treat any of (numeric coords, non-empty
  // address) as «verified».
  const hasNumericCoords = ps.geoVerifiedLat != null && ps.geoVerifiedLon != null;
  const hasAddress = ps.geoVerifiedAddress != null && ps.geoVerifiedAddress.length > 0;
  const geoFromServer: { lat: number | null; lon: number | null; address?: string } | undefined =
    (hasNumericCoords || hasAddress)
      ? {
          lat: hasNumericCoords ? ps.geoVerifiedLat! : null,
          lon: hasNumericCoords ? ps.geoVerifiedLon! : null,
          ...(hasAddress ? { address: ps.geoVerifiedAddress! } : {}),
        }
      : undefined;
  // Compare against local — only setState if a meaningful difference exists.
  const localGeo = state.geoVerified;
  const geoStateMismatch =
    (geoFromServer === undefined) !== (localGeo === undefined) ||
    (geoFromServer !== undefined && localGeo !== undefined && (
      geoFromServer.lat !== localGeo.lat ||
      geoFromServer.lon !== localGeo.lon ||
      (geoFromServer.address ?? null) !== (localGeo.address ?? null)
    ));
  // Already in sync — fast path. Still re-anchor if the local timer is
  // meaningfully out of step with server (>1 sec drift). Or sync the
  // geo-verified state if server says the team has already verified
  // (Capt B coming back after Capt A's geo-verify) but local doesn't
  // know — flip phase to site-code.
  if (serverIdx === state.currentLocationIndex) {
    const needsAnchorFix = anchorDriftMs > 1000 && ps.locationEnteredAt != null;
    // /investigate 2026-04-27: only sync geoVerified IN to local. Don't
    // overwrite a local geoVerified with server's (server might be 1-tick
    // behind a just-fired GEO_VERIFIED that the local already saw via WS).
    // The bug we're closing is local=undefined + server=verified, not the
    // reverse. So only the local=undefined → server-has-it direction.
    const needsGeoSync = localGeo === undefined && geoFromServer !== undefined;
    if (needsAnchorFix || needsGeoSync) {
      const patch: Partial<typeof state> = {};
      if (needsAnchorFix) patch.locationStartedAt = reanchored;
      if (needsGeoSync) patch.geoVerified = geoFromServer;
      setState(patch);
      return { changed: true, finished: false };
    }
    // Tracked but no-op — the geo-state-mismatch branches we DON'T act on
    // (server lagging behind local; both undefined; both equal) just
    // fast-path. The unused `geoStateMismatch` flag stays for future
    // diagnostic logging if a regression surfaces.
    void geoStateMismatch;
    return NO_CHANGE;
  }
  // Server disagrees. Trust the server. Apply key + encrypted riddle from
  // the server response so the next render can decrypt without falling
  // through to the bottom-of-render recovery branch (defense-in-depth).
  if (ps.nextRiddleKey) {
    const newKeys = [...state.nextKeys];
    if (serverIdx > 0) newKeys[serverIdx - 1] = ps.nextRiddleKey;
    const newLocations = [...state.locations];
    const targetLoc = newLocations[serverIdx];
    if (targetLoc && ps.encryptedRiddleText) {
      newLocations[serverIdx] = {
        ...targetLoc,
        encryptedRiddle: ps.encryptedRiddleText,
        encryptedHint: ps.encryptedHintText || '',
        // v0.80.5 — multi-hint reconcile. Pre-fix: only riddle + legacy
        // single hint were updated, the multi-hint array kept its stale
        // (or empty) state from before the missed ADVANCE. Result:
        // raskryvayushchiesya hints silently failed for any captain
        // reconnecting after a teammate advanced the team. Fall back to
        // the existing local array when server returned empty/undefined
        // (legacy server, or location without hints) — never overwrite
        // a populated local array with empty.
        encryptedHints: (ps.encryptedHints && ps.encryptedHints.length > 0)
          ? ps.encryptedHints
          : targetLoc.encryptedHints,
      };
    }
    reconcileLocationIndex(serverIdx, {
      nextKeys: newKeys,
      locations: newLocations,
      firstRiddleKey: serverIdx === 0 ? ps.nextRiddleKey : state.firstRiddleKey,
      // Re-anchor on stage change. Falls back to Date.now() if the server
      // didn't include locationEnteredAt (legacy responses).
      locationStartedAt: reanchored,
      // /investigate 2026-04-27 prod 734032 — geo-verify state is server-
      // authoritative now. After advance to N+1, server might already
      // know the team's verified N+1 too (Capt A geo-verified BOTH
      // before B reopens). Pre-fix: hardcoded undefined → B re-verifies.
      geoVerified: geoFromServer,
      hintNavigateData: undefined,
    });
    return { changed: true, finished: false };
  }
  return NO_CHANGE;
}

/**
 * Cold-mount reconcile entry point. Called directly from `renderLocationView`
 * at the top of the function. Intentionally NOT lock-gated: at cold-mount time
 * the lock could be stale from a prior view that didn't clean up, or from a
 * stuck inflight fetch. Cold mount must always fire a fresh fetch. The
 * AbortController + 15s timeout still applies (via `runWithoutLock`).
 */
export async function reconcileFromServer(): Promise<{ changed: boolean; finished: boolean; authFailed?: boolean }> {
  return locationGate.runWithoutLock(NO_CHANGE, reconcileBody);
}

export async function renderLocationView(container: HTMLElement): Promise<void> {
  // Approach D companion guard — defense in depth for the resumeGame
  // limbo edge case. If our cached state has currentLocationIndex past
  // the loaded locations array, results is the only sane render. The
  // reconcile below will catch up via ps.finishedAt branch (line ~307)
  // and confirm 'results' is correct, but we don't make the user watch
  // the limbo screen for the round-trip.
  const localState = getState();
  if (localState.locations.length > 0
      && localState.currentLocationIndex >= localState.locations.length) {
    navigate('results');
    return;
  }

  // Session 89 — reconcile against server before any rendering. Closes the
  // captain-desync bug: capt A submits → ADVANCE broadcasts → capt B was
  // offline → on reopen B was stuck on the old riddle until the next
  // ADVANCE event. Now B fetches /player-state on cold mount and
  // catches up.
  const reconcile = await reconcileFromServer();
  if (reconcile.authFailed) {
    // v0.79.18 — auth-failure handler in main.ts has already torn down
    // listeners + navigated. Bail without continuing render.
    return;
  }
  if (reconcile.finished) {
    navigate('results');
    return;
  }
  if (reconcile.changed) {
    // State updated — re-enter render with fresh state.
    return renderLocationView(container);
  }

  // Plan B Task 8 (2026-05-11) — visibility/pageshow lifecycle is delegated
  // to `activeLc`. The composite callback to the visibility-reconciler
  // installer routes through `gatedReconcileFromServer` and handles the
  // r.finished + r.changed fan-out (results-navigate / destroy-before-
  // render). NO `ws?.forceReconnect()` here: location-view does NOT own
  // its own WebSocket — the team WS lives in `pwa/src/api/team-sync.ts`
  // as module-level `currentWs`, and team-sync owns its own visibility
  // + pageshow handlers + forceReconnect calls. Out-of-scope per the
  // Task 8 design memo.
  //
  // The pre-extraction visibilityHandler / pageshowHandler shape (cleanup-
  // then-attach idempotence, polarity bail on !visible / !persisted, the
  // symmetric removeEventListener pair in destroy) is preserved by the
  // installer + `lc.cleanup()`. The same-route guard (#/riddle) lives
  // INSIDE the installer where call sites can't drop it; the .finished /
  // .changed fan-out stays in the callback because it's specific to the
  // location-view's recovery shape (vs the lobby/chat/spectator views
  // which don't need a results-navigate or destroy-before-render branch).
  activeLc = createViewLifecycle();

  // Plan B Task 8 (2026-05-11) — module-scoped handles cleaned via lc.onDestroy
  // so cleanup reads the LATEST reference at teardown time. timerHandle and
  // menuHandle rebuild on every phase transition (renderRiddlePhase /
  // renderInputPhase / renderSiteCodePhase each create their own); copyDisposers
  // accumulates in renderSiteCodePhase; teamEventUnsubscribe is set once in
  // this render and is mutated only by the next render (which reassigns it
  // via `teamEventUnsubscribe?.(); teamEventUnsubscribe = onTeamEvent(...)`).
  // hintTimer + progressTimer are created/recreated by phase transitions in
  // the `clear → create` pattern, so individual-cancel semantics live in the
  // phase code; lc.onDestroy clears whatever is the latest reference at
  // teardown to drop the live timer.
  activeLc.onDestroy(() => {
    if (hintTimer) { clearInterval(hintTimer); hintTimer = null; }
    if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
    timerHandle?.destroy();
    timerHandle = null;
    menuHandle?.destroy();
    menuHandle = null;
    copyDisposers.forEach(d => d());
    copyDisposers = [];
    releaseChatBadgeSubscription();
    teamEventUnsubscribe?.();
    teamEventUnsubscribe = null;
  });

  installVisibilityReconcile(activeLc, () => {
    void gatedReconcileFromServer().then((r) => {
      if (r.finished) {
        // Same-hash guard symmetric with poll-player-state.ts:105.
        if (typeof window === 'undefined' || window.location.hash !== '#/results') {
          navigate('results');
        }
      } else if (r.changed) {
        // State drifted while suspended — full re-mount. destroy FIRST to
        // tear down setInterval timers (hintTimer, progressTimer) that the
        // upcoming render will re-create. Without destroy, the new render's
        // `hintTimer = setInterval(...)` overwrites the module-level ref
        // while the OLD interval keeps ticking — two timers fighting over
        // `#loc-hint-countdown.style.display` produce «hint countdown text
        // flickering once per second» (user-reported v0.79.14 post-deploy).
        // Same memory class as `learning_module_let_concurrent_render.md`.
        destroyLocationView();
        void renderLocationView(container);
      }
    });
  }, '#/riddle');

  const state = getState();
  const loc = state.locations[state.currentLocationIndex];
  if (!loc) {
    navigate(state.locations.length === 0 ? 'join' : 'results');
    return;
  }

  // Session 50 §5d.bis — subscribe to team events for this view. Handler
  // fires when ANY teammate advances, catches up offline, or finishes.
  // Registered here (after mount), torn down in destroyLocationView.
  teamEventUnsubscribe?.();
  teamEventUnsubscribe = onTeamEvent((evt) => {
    const s = getState();
    // Read the current draft from whichever site-code input the player is
    // typing in — if any have non-empty text, we use the mid-typing banner
    // variant that preserves their work instead of silently teleporting.
    const codeInputs = Array.from(
      container.querySelectorAll<HTMLInputElement>('input[name="siteCode"]')
    );
    const draft = codeInputs.map(i => i.value.trim()).filter(v => v.length > 0).join(' | ');

    // Seed `nextKeys` from the event payload (session-44 resume-fix class)
    // so navigating to the new location can actually decrypt its riddle.
    function applyNextKey(targetIdx: number, key?: string | null) {
      if (!key || targetIdx <= 0) return;
      const nextKeys = [...s.nextKeys];
      nextKeys[targetIdx - 1] = key;
      setState({ nextKeys });
    }

    // /investigate 2026-04-26 — production game 566763, team Кодоломы.
    // The submitter's local advance path (auto-advance.ts:125-137 and the
    // correct-answer success branch in this file) mirrors `nextEncryptedRiddle`
    // from the HTTP response into `state.locations[target].encryptedRiddle`.
    // The WS-driven advance path for non-submitter teammates was missing the
    // sibling mirror — only `applyNextKey` ran. After `goTo` bumped
    // `currentLocationIndex`, those teammates landed on the new index with
    // `state.locations[target].encryptedRiddle = ""` (online-mode join strips
    // it for `i > 0`, see GameJoinOps.kt:325) → `decrypt("", key)` threw →
    // 'Ошибка дешифрования загадки'. Hits CAS-loser in TIMEOUT races, plain
    // teammate-watching, and server-driven scheduler advances.
    function applyNextEncryptedRiddle(
      targetIdx: number,
      encRiddle?: string | null,
      encHint?: string | null,
      encHints?: EncryptedHint[]
    ) {
      if (!encRiddle || targetIdx <= 0 || targetIdx >= s.locations.length) return;
      const newLocations = [...s.locations];
      newLocations[targetIdx] = {
        ...newLocations[targetIdx],
        encryptedRiddle: encRiddle,
        encryptedHint: encHint ?? newLocations[targetIdx].encryptedHint,
        encryptedHints: encHints && encHints.length > 0 ? encHints : newLocations[targetIdx].encryptedHints,
      };
      setState({ locations: newLocations });
    }

    // Session 50 post-ship fix: when following a teammate's advance, use
    // the server-supplied `locationEnteredAt` as the new timer anchor so
    // all teammates (including late joiners who arrive mid-location) share
    // the same elapsed time + hint-delay schedule.
    //
    // Post-ship fix #2: translate server origin into THIS device's clock
    // frame via `computeLocalAnchor(serverOrigin, serverNow)`. Without the
    // translation, a device whose clock drifts from the server would show
    // drift-seconds wrong elapsed. Falls back to Date.now() only when the
    // server didn't attach either (should not happen post-fix).
    const goTo = (idx: number, serverOrigin?: number | null, serverNow?: number | null) => {
      if (idx >= s.locations.length) {
        navigate('results');
      } else {
        const anchor = anchorFromServer(serverOrigin, serverNow);
        advanceLocationIndex(idx, {
          locationStartedAt: anchor,
        });
        navigate('riddle');
      }
    };

    if (evt.type === 'ADVANCE' && typeof evt.newIndex === 'number') {
      const target = evt.newIndex;
      // Session 71 — self-echo suppression. Submitter receives its own
      // ADVANCE broadcast ~100ms after HTTP response. Without this guard
      // the WS path fires `handleAdvanceEvent` (1.5s auto-toast → goTo →
      // re-mount) AND the local `setTimeout(2000)` in doSubmit ALSO fires
      // (→ navigate → re-mount). Two re-mounts within 500ms produce the
      // visible «мерцает 2-3 раза» screen-enter flash user reported
      // 2026-04-23. Server now stamps `submitterPlayerId` on every ADVANCE
      // TeamEvent (GamePlayOps.kt:213,468); we drop the frame if it's our
      // own. Symmetric with the session-70 GEO_VERIFIED self-echo guard
      // below. Using server-authoritative `Players.id`, not nickname.
      //
      // Post-guard: the index guard below still catches idempotent replays
      // (same-team stale WS after local setTimeout already advanced us).
      if (evt.submitterPlayerId && evt.submitterPlayerId === s.playerId) return;
      // Don't fire the advance UX for our own advance — we're already the
      // writer, the navigate already happened via the submit-success path.
      if (target <= s.currentLocationIndex) return;
      applyNextKey(target, evt.nextRiddleKey);
      applyNextEncryptedRiddle(target, evt.nextEncryptedRiddle, evt.nextEncryptedHint, evt.nextEncryptedHints);
      handleAdvanceEvent(evt, {
        draft,
        onAdvance: () => goTo(target, evt.locationEnteredAt, evt.serverNow),
      }, activeLc!);
    } else if (evt.type === 'CAUGHT_UP' && typeof evt.toIndex === 'number') {
      const target = evt.toIndex;
      if (target <= s.currentLocationIndex) return;
      applyNextKey(target, evt.nextRiddleKey);
      applyNextEncryptedRiddle(target, evt.nextEncryptedRiddle, evt.nextEncryptedHint, evt.nextEncryptedHints);
      handleCaughtUpEvent(evt, {
        onCompleteSync: () => goTo(target, evt.locationEnteredAt, evt.serverNow),
      }, activeLc!);
    } else if (evt.type === 'FINISH') {
      // Session 67 post-ship /investigate — seed results-screen totals from
      // the FINISH payload. Without this, a non-submitting teammate (or a
      // player force-finished via `orgFinishTeam`) lands on the results
      // view with `state.totalTimeMs=0` because only the submitter's local
      // completion path writes that field. User-reported 2026-04-22: «В
      // лидерборде у команды Базовое время 0, итого 0» on autokvest.ru.
      //
      // Formula matches the backend leaderboard: `totalTimeMs =
      // (finishedAt - startedAt) + penaltyMs`. All three values travel on
      // the FINISH wire payload; penaltyMs falls back to local if server
      // didn't send it (defensive — legacy FINISH shapes lack penaltyMs).
      if (typeof evt.finishedAt === 'number' && typeof evt.startedAt === 'number') {
        const penalty = typeof evt.penaltyMs === 'number' ? evt.penaltyMs : s.penaltyMs;
        // Session 68 /review finding #2 — server clock cannot produce
        // finishedAt < startedAt under normal operation (single JVM
        // System.currentTimeMillis() is monotonic in practice). If we ever
        // see it, it's a DB restore inconsistency or operator UPDATE and
        // the Math.max(0, ...) below silently renders «Базовое 00:00».
        // Surface it to the console so a support session can spot the
        // corruption instead of assuming the UI is broken.
        if (evt.finishedAt < evt.startedAt) {
          console.warn('[lobby-view] FINISH payload has finishedAt < startedAt',
            { finishedAt: evt.finishedAt, startedAt: evt.startedAt, skewMs: evt.startedAt - evt.finishedAt });
        }
        advanceLocationIndex(s.locations.length, {
          status: 'FINISHED',
          completedAt: evt.finishedAt,
          penaltyMs: penalty,
          totalTimeMs: Math.max(0, evt.finishedAt - evt.startedAt) + Math.max(0, penalty),
        });
      }
      // 2026-04-26 user request — drop the «Продолжить осмотр / К
      // результатам» CTA toast. Pre-fix, every non-submitter teammate
      // received a modal-like toast with two buttons; user found it
      // redundant since «все игроки команды и так автоматически
      // переносятся к результатам» is the natural expectation when the
      // game ends. Submitter still auto-navigated via their local
      // submit-success path; teammates now also auto-navigate. Mid-typing
      // / mid-upload work would be lost on auto-cut, but the game has
      // ended — those drafts are moot anyway.
      //
      // Self-echo suppression preserved for the submitter: their local
      // path already called `navigate('results')`, so re-firing here
      // would just be a no-op double-navigate. Skip the redundant call
      // for clarity (defensive — `navigate` itself is idempotent).
      if (evt.submitterPlayerId && evt.submitterPlayerId === s.playerId) {
        // No-op: submitter already navigated via their local submit-success path.
      } else {
        navigate('results');
      }
    } else if (evt.type === 'PENALTY') {
      // Session 66 — organizer added/removed penalty from the Monitoring kebab.
      // Toast shows signed delta + new cumulative total. No view change.
      handlePenaltyEvent(evt, activeLc!);
    } else if (evt.type === 'GEO_VERIFIED' && typeof evt.locationIndex === 'number') {
      // Session 50 post-ship fix — live teammate found the place. Flip
      // captain / other-teammate UI to site-code phase with the same
      // verified geo data so they don't have to re-submit their own
      // coordinates or address.
      //
      // Guards (order matters):
      //   1. Self-echo (session 70 #6): if THIS PWA submitted the answer,
      //      the local API success handler already rendered green
      //      «МЕСТО НАЙДЕНО» + advanced to site-code. Showing a toast
      //      «Игрок X нашёл место!» with the submitter's own nickname is
      //      confusing — attribution for teammates only. Using server-
      //      authoritative playerId, not nickname matching (nickname can
      //      be blank / auto «Капитан #ABCD» / etc.).
      //   2. Wrong-location: we're ahead or behind, ignore.
      //   3. Already at site-code: another independent verification beat
      //      this WS frame — don't clobber the ongoing transition.
      if (evt.submitterPlayerId && evt.submitterPlayerId === s.playerId) return;
      if (evt.locationIndex !== s.currentLocationIndex) return;
      if (phase === 'site-code') return;
      handleGeoVerifiedEvent(evt, {
        onAdvance: () => {
          // /investigate 2026-04-27 follow-up — ADDRESS-type stages without
          // geocoded target coords broadcast `lat=null, lon=null, address="…"`.
          // Pre-fix the `typeof === 'number'` gate skipped the entire setState
          // → teammate flipped phase but `state.geoVerified` stayed
          // undefined → empty МЕСТО НАЙДЕНО card AND map button hidden.
          // Address alone is a valid verification — accept any of (numeric
          // coords, non-empty address). `null` coords pass straight through
          // and renderSiteCodePhase + buildYandexMapsUrl already tolerate
          // them (coord card hides; map URL falls back to address text).
          const evtLat = typeof evt.lat === 'number' ? evt.lat : null;
          const evtLon = typeof evt.lon === 'number' ? evt.lon : null;
          const evtAddr = evt.address ?? null;
          if (evtLat !== null || evtLon !== null || evtAddr) {
            // Persistent store — survives view re-mounts.
            setState({
              geoVerified: {
                lat: evtLat,
                lon: evtLon,
                address: evtAddr ?? undefined,
              },
            });
            // Mirror into module-locals so the FIRST render (before
            // renderSiteCodePhase's defensive refresh kicks in) sees the
            // fresh values. renderSiteCodePhase still re-reads from
            // state.geoVerified at entry (session-50 Bug 3 / v0.79.10
            // pattern) — this assignment is a render-input cache, not
            // the source of truth.
            lastAnswerLat = evtLat;
            lastAnswerLon = evtLon;
            lastAnswerAddress = evtAddr;
          }
          phase = 'site-code';
          render();
        },
      }, activeLc!);
    }
  });

  // --- Decryption (once per mount) ---
  // Session 53 post-ship — if the key chain is incomplete on mount (the
  // session-53 auto-advance race could bump currentLocationIndex past the
  // last populated entry in `nextKeys`), try once to recover by asking
  // `/player-state` for the current team's authoritative key + encrypted
  // riddle BEFORE falling through to `navigate('results')`. That endpoint
  // returns `nextRiddleKey` for whatever currentLocationIndex the server
  // knows about; if it's > 0 we seed `nextKeys[currentLocationIndex-1]`
  // and continue. Navigating to results with a stuck local state was the
  // user-visible symptom (F5 after demo flow → "КВЕСТ ПРОЙДЕН" screen
  // despite the backend team still being mid-game).
  let key: Uint8Array;
  if (state.currentLocationIndex === 0) {
    key = keyFromBase64(state.firstRiddleKey);
  } else {
    let prevKey: string | undefined = state.nextKeys[state.currentLocationIndex - 1];
    if (!prevKey) {
      // Retro-audit 2026-04-30 closed via the v0.79.18 dispatch flow + the
      // wrapper added in this deepening: the «capture status outside try,
      // dispatch outside catch» dance is gone — fetchPlayerState dispatches
      // before any caller-visible try/catch can swallow it.
      const result = await fetchPlayerState({
        gameCode: state.gameCode,
        playerId: state.playerId,
        playerToken: state.playerToken,
        context: 'prevkey-recovery',
      });
      if (result.kind === 'auth-failed') {
        // Wrapper already dispatched aq:auth-permanent-failure. main.ts
        // handler will tear down + navigate('join'). Just return.
        return;
      }
      if (result.kind === 'ok') {
        const ps = result.data;
        // Server says the team has already finished — let the normal
        // completion path take over (keeps totalTimeMs + completedAt in
        // sync with the leaderboard).
        if (ps.finishedAt) {
          advanceLocationIndex(ps.currentLocationIndex, {
            completedAt: ps.finishedAt,
            status: 'FINISHED',
          });
          navigate('results');
          return;
        }
        // Server may disagree with us on currentLocationIndex — trust it.
        // If it says we're at a LOWER index (client optimistically over-
        // advanced), re-seed both the index AND the key in one setState.
        const serverIdx = ps.currentLocationIndex;
        if (typeof serverIdx === 'number' && ps.nextRiddleKey) {
          const newKeys = [...state.nextKeys];
          if (serverIdx > 0) newKeys[serverIdx - 1] = ps.nextRiddleKey;
          const newLocations = [...state.locations];
          const targetLoc = newLocations[serverIdx];
          if (targetLoc && ps.encryptedRiddleText) {
            newLocations[serverIdx] = {
              ...targetLoc,
              encryptedRiddle: ps.encryptedRiddleText,
              encryptedHint: ps.encryptedHintText || '',
            };
          }
          reconcileLocationIndex(serverIdx, {
            nextKeys: newKeys,
            locations: newLocations,
            firstRiddleKey: serverIdx === 0 ? ps.nextRiddleKey : state.firstRiddleKey,
          });
          // Re-enter render with the reconciled state.
          renderLocationView(container);
          return;
        }
      }
      // network-blip OR ok-with-no-actionable-data → fall through to results.
      console.error('Missing next key for decryption and recovery failed');
      navigate('results');
      return;
    }
    key = keyFromBase64(prevKey);
  }

  let riddleText: string;
  try {
    riddleText = await decrypt(loc.encryptedRiddle, key);
  } catch {
    riddleText = 'Ошибка дешифрования загадки';
  }

  interface DecryptedHint { text: string; delaySec: number; order: number; type?: string; }
  const decryptedHints: DecryptedHint[] = [];
  const hints = loc.encryptedHints || [];
  if (hints.length > 0) {
    for (const h of hints) {
      try {
        const text = h.type === 'auto_navigate' ? '' : await decrypt(h.encryptedText, key);
        decryptedHints.push({ text, delaySec: h.delaySec, order: h.orderIndex, type: h.type });
      } catch (err) {
        console.warn('[location] hint decrypt failed', err);
      }
    }
  } else if (loc.encryptedHint) {
    try {
      const text = await decrypt(loc.encryptedHint, key);
      decryptedHints.push({ text, delaySec: loc.hintDelaySec, order: 0 });
    } catch (err) {
      console.warn('[location] legacy hint decrypt failed', err);
    }
  }
  decryptedHints.sort((a, b) => a.delaySec - b.delaySec);

  const hintText = decryptedHints[0]?.text || '';
  setState({ currentRiddleText: riddleText, currentHintText: hintText });

  // --- Phase state ---
  let phase: Phase = 'input';
  const activeTab: 'coords' | 'address' = loc.answerType === 'COORDINATES' ? 'coords' : 'address';

  // FIAS selection state
  let selectedCityId: number | null = null;
  let selectedStreetId: number | null = null;
  let selectedBuildingId: number | null = null;

  // Last verified answer
  let lastAnswerLat: number | null = null;
  let lastAnswerLon: number | null = null;
  let lastAnswerAddress: string | null = null;

  // Track which hints have been revealed (persists across phase switches)
  const revealedHints: boolean[] = decryptedHints.map(() => false);

  // Restore site-code phase from hint-navigate or verified geo
  const navData = state.hintNavigateData;
  const verified = state.geoVerified;
  if (navData) {
    setState({ hintNavigateData: undefined });
    lastAnswerLat = navData.lat;
    lastAnswerLon = navData.lon;
    if (navData.city) lastAnswerAddress = `${navData.city}, ${navData.street || ''} ${navData.house || ''}`.trim();
    setState({ geoVerified: { lat: navData.lat, lon: navData.lon, address: lastAnswerAddress || undefined } });
    phase = 'site-code';
  } else if (verified) {
    lastAnswerLat = verified.lat;
    lastAnswerLon = verified.lon;
    lastAnswerAddress = verified.address || null;
    phase = 'site-code';
  }

  // --- Org-advance polling (online only, shared across all phases) ---
  // Session 53 /investigate — poll logic extracted to `pollPlayerStateOnce`
  // so every branch (finish / advance / no-change / fetch-error) is
  // covered by direct unit tests instead of living inline as dead-
  // untestable code. Bug that sparked the extraction: user-reported
  // "organizer clicks advance → player doesn't move" with zero tests
  // exercising the path.
  if (!state.fullOffline) {
    // Fire one poll immediately on mount so an organizer's advance that
    // happened moments before mount (e.g. right before F5, or while the
    // player was in chat) propagates instantly instead of waiting up to
    // five seconds for the first setInterval tick. Session 53 user
    // report: "org clicked advance → player didn't move" — the poll
    // eventually catches up, but a ≤5s latency reads as "broken" when
    // the player is staring at the old screen.
    void pollPlayerStateOnce(state.gameCode, state.playerId, state.playerToken);
    // Plan B Task 8 (2026-05-11) — interval registered via activeLc.setInterval
    // so destroy releases it. The pre-extraction closure-local `myPollId` +
    // module-level `orgAdvancePoll` self-terminate defense (/investigate
    // 2026-04-26 prod game 757873) is collapsed: lc.setInterval captures the
    // id internally and cleanup() clears it on view teardown. Once a poll
    // sees `outcome === 'finished'` we navigate to /results — destroyLocationView
    // fires from the router's view swap, which calls activeLc.cleanup() and
    // clears this interval. The deeper concurrent-mount leak class (race
    // between `main.ts navigate(target)` + `initRouter().handleRoute()`)
    // is now Plan A's territory (view-lifecycle coordinator); for Plan B
    // the lc cleanup matches the pre-extraction destroy-side guarantee.
    activeLc.setInterval(() => {
      void (async () => {
        const s = getState();
        const outcome = await pollPlayerStateOnce(s.gameCode, s.playerId, s.playerToken);
        if (outcome === 'finished') {
          // No need to clear here — lc.cleanup() is already running via the
          // upcoming destroyLocationView triggered by the navigate('results')
          // inside pollPlayerStateOnce. Even if pollPlayerStateOnce didn't
          // navigate (it does — see poll-player-state.ts), the next tick
          // would still hit the same finished outcome harmlessly.
        }
      })();
    }, 5000);
  }

  // --- Hint reveal interval (shared across riddle + input phases) ---
  function updateHintReveals(): void {
    const startedAt = getState().locationStartedAt;
    if (!startedAt) return;
    const elapsed = startedAt ? getElapsedSec(startedAt) : Infinity;
    decryptedHints.forEach((h, idx) => {
      if (h.delaySec === 0 || elapsed >= h.delaySec) {
        revealedHints[idx] = true;
      }
    });
  }

  // Initial reveal check
  updateHintReveals();

  // Start hint reveal interval
  if (decryptedHints.length > 0 && !revealedHints.every(Boolean)) {
    hintTimer = setInterval(() => {
      const before = revealedHints.filter(Boolean).length;
      updateHintReveals();
      const after = revealedHints.filter(Boolean).length;
      if (after > before) {
        // New hints revealed — update DOM if hints container is visible
        renderHintsIntoContainer();
        renderHintCountdown();
      }
      if (revealedHints.every(Boolean)) {
        if (hintTimer) { clearInterval(hintTimer); hintTimer = null; }
      }
    }, 1000);
  }

  /** Render hint cards into the hints container (called on reveal and on phase render). */
  function renderHintsIntoContainer(): void {
    const hintsContainer = container.querySelector<HTMLElement>('#loc-hints-container');
    if (!hintsContainer) return;
    const totalHints = decryptedHints.length;
    if (totalHints === 0) return;

    // Build or update hint cards
    decryptedHints.forEach((h, idx) => {
      let card = hintsContainer.querySelector<HTMLElement>(`#loc-hint-${idx}`);
      if (!card) {
        card = document.createElement('div');
        card.className = 'card';
        card.id = `loc-hint-${idx}`;
        card.style.marginBottom = 'var(--sp-md)';
        card.style.display = 'none';
        const label = totalHints > 1 ? `ПОДСКАЗКА ${idx + 1} ИЗ ${totalHints}` : 'ПОДСКАЗКА';
        if (h.type === 'auto_navigate') {
          // Session 53 UX request — the "show place" button is visually
          // loud (btn-primary full-width). Only the captain should see
          // the action; regular teammates see a neutral explanation
          // instead so they know the captain can reveal coordinates.
          if (getState().isCaptain) {
            card.innerHTML = `<p class="text-eyebrow" style="margin-bottom: var(--sp-sm);">${label}</p>
              <button class="btn btn-secondary btn-full hint-navigate-btn">ПОКАЗАТЬ МЕСТО</button>`;
          } else {
            card.innerHTML = `<p class="text-eyebrow" style="margin-bottom: var(--sp-sm);">${label}</p>
              <p class="text-body" style="color: var(--text-secondary);">Капитан может открыть координаты.</p>`;
          }
        } else {
          card.innerHTML = `<p class="text-eyebrow" style="margin-bottom: var(--sp-sm);">${label}</p>
            <p class="text-body">${escapeHtml(h.text)}</p>`;
        }
        hintsContainer.appendChild(card);
      }
      card.style.display = revealedHints[idx] ? 'block' : 'none';
    });

    // Wire auto-navigate hint buttons
    hintsContainer.querySelectorAll<HTMLButtonElement>('.hint-navigate-btn').forEach(btn => {
      if (btn.dataset.wired) return;
      btn.dataset.wired = '1';
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = 'Загрузка...';
        try {
          const { hintNavigate } = await import('../api/game-api');
          const { clearAutoAdvanceForLocation } = await import('../utils/auto-advance');
          const s = getState();
          const data = await hintNavigate(s.gameCode, s.playerId, loc.id);
          clearAutoAdvanceForLocation(loc.id);
          lastAnswerLat = data.lat;
          lastAnswerLon = data.lon;
          if (data.city) lastAnswerAddress = `${data.city}, ${data.street || ''} ${data.house || ''}`.trim();
          setState({ geoVerified: { lat: data.lat, lon: data.lon, address: lastAnswerAddress || undefined } });
          phase = 'site-code';
          render();
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'ПОКАЗАТЬ МЕСТО';
          console.error('[location] hint-navigate failed', err);
        }
      });
    });
  }

  /** Update the hint countdown display. */
  function renderHintCountdown(): void {
    const hintCountdown = container.querySelector<HTMLElement>('#loc-hint-countdown');
    const hintSeconds = container.querySelector<HTMLElement>('#hint-seconds');
    if (!hintCountdown || !hintSeconds) return;

    const nextUnrevealed = revealedHints.findIndex(r => !r);
    if (nextUnrevealed < 0) {
      hintCountdown.style.display = 'none';
      return;
    }

    hintCountdown.style.display = 'block';
    const startedAt = getState().locationStartedAt;
    const elapsed = startedAt ? getElapsedSec(startedAt) : 0;
    const remaining = Math.max(0, decryptedHints[nextUnrevealed].delaySec - elapsed);

    if (remaining >= 300) {
      hintSeconds.textContent = Math.ceil(remaining / 60) + ' мин';
    } else {
      const m = Math.floor(remaining / 60);
      const s = Math.ceil(remaining % 60);
      hintSeconds.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
  }

  // Hint countdown update interval (1 sec)
  if (decryptedHints.length > 0 && !revealedHints.every(Boolean)) {
    // The hintTimer above handles reveals; this piggybacks on it for countdown display.
    // If hintTimer was already set, the countdown update is done inside it.
    // If all hints are already revealed, no countdown needed.
  }

  render();

  // --- Phase dispatcher ---
  function render(): void {
    // Clean up per-phase handles before re-render
    timerHandle?.destroy();
    timerHandle = null;
    menuHandle?.destroy();
    menuHandle = null;
    copyDisposers.forEach(d => d());
    copyDisposers = [];
    if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
    // Cancel any pending demo-bot fire from the prior phase so a phase
    // transition doesn't let the previous phase's bot click into this one's
    // DOM. scheduleDemoBotAction below re-arms for the new phase.
    cancelDemoBot();

    if (phase === 'riddle') renderRiddlePhase();
    else if (phase === 'input') renderInputPhase();
    else renderSiteCodePhase();

    // Demo autopilot — no-op when state.demoScript is empty (real-player
    // joins). For demo runs (org-token gated createDemoSession) the bot
    // fires the appropriate click for the current phase after a computed
    // delay (FIRE_AT_REMAINING_SEC of in-game time, scaled by demo speed).
    scheduleDemoBotAction(phase, container);
  }

  // ===================================================================
  //  RIDDLE PHASE
  // ===================================================================
  function renderRiddlePhase(): void {
    container.innerHTML = `
      <div class="screen screen-enter screen-split">
        <div class="screen-content">
          <div style="display: flex; align-items: center; gap: var(--sp-sm); margin-bottom: var(--sp-md);">
            <div style="flex: 1 1 0;"></div>
            <div id="loc-timer" style="flex: 0 0 auto;"></div>
            <div style="flex: 1 1 0; display: flex; justify-content: flex-end; align-items: center; gap: var(--sp-sm);">
              ${state.fullOffline ? '<span class="badge" style="background: var(--text-hint); font-size: 10px; padding: 2px 8px;">ОФФЛАЙН</span>' : ''}
              <div id="loc-menu"></div>
            </div>
          </div>

          <div style="margin-bottom: var(--sp-sm);">
            <div class="progress-bar">
              <div id="loc-progress" class="progress-bar__fill" style="width: 100%; background: var(--success);"></div>
            </div>
          </div>

          <p class="text-eyebrow" style="margin-bottom: var(--sp-sm);">ЗАГАДКА</p>
          ${renderMedia(loc.mediaUrl, loc.mediaType, !!riddleText, loc.additionalMedia)}
          ${riddleText ? `<p class="text-body-large" style="margin-bottom: var(--sp-lg); white-space: pre-wrap;">${escapeHtml(riddleText)}</p>` : ''}

          <!-- Hints live under the riddle (session 47e): gives the player the
               puzzle text first, then reveals help as they unfold. -->
          <div id="loc-hint-countdown" class="text-body" style="color: var(--text-hint); text-align: center; margin-bottom: var(--sp-md); display: none;">
            Подсказка через <span id="hint-seconds"></span>
          </div>
          <div id="loc-hints-container"></div>

          <div class="spacer"></div>
        </div>

        <div class="screen-controls">
          <button id="loc-answer-btn" class="btn btn-primary btn-full" data-testid="answer-btn">ОТВЕТИТЬ</button>
          ${state.canChat ? `<button id="loc-chat-btn" class="btn btn-secondary btn-full" style="position: relative;">
            Чат
            <span id="loc-chat-badge" class="badge" style="position: absolute; top: -8px; right: -8px; display: none;"></span>
          </button>` : ''}
        </div>
      </div>
    `;

    // Timer
    const timerContainer = container.querySelector<HTMLElement>('#loc-timer')!;
    timerHandle = createTimer(timerContainer, loc.timeLimitSec, () => {
      triggerAdvance('TIMEOUT', activeLc!);
    });

    // Menu
    menuHandle = createGameMenu(container.querySelector('#loc-menu')!, activeLc!, { allowSkip: true });

    // Progress bar
    const progressEl = container.querySelector<HTMLElement>('#loc-progress')!;
    progressTimer = setInterval(() => {
      const startedAt = getState().locationStartedAt;
      if (!startedAt) return;
      const elapsed = getElapsedSec(startedAt);
      const pct = Math.max(0, Math.min(100, (1 - elapsed / loc.timeLimitSec) * 100));
      progressEl.style.width = pct + '%';
      const ratio = pct / 100;
      if (ratio < 0.25) progressEl.style.background = 'var(--danger)';
      else if (ratio < 0.5) progressEl.style.background = 'var(--amber)';
      else progressEl.style.background = 'var(--success)';
    }, 500);

    // Hints
    renderHintsIntoContainer();
    renderHintCountdown();

    // Update hint countdown every second (piggyback on existing hintTimer or set up separate)
    if (decryptedHints.length > 0 && !revealedHints.every(Boolean)) {
      // hintTimer already runs every 1s for reveal checks.
      // But we also need to update the countdown text. The hintTimer callback
      // calls renderHintCountdown on new reveals. For smooth countdown between
      // reveals, we piggyback on the hint countdown update in the interval.
      // Override the interval to also update countdown:
      if (hintTimer) { clearInterval(hintTimer); hintTimer = null; }
      hintTimer = setInterval(() => {
        const before = revealedHints.filter(Boolean).length;
        updateHintReveals();
        const after = revealedHints.filter(Boolean).length;
        if (after > before) {
          renderHintsIntoContainer();
        }
        renderHintCountdown();
        if (revealedHints.every(Boolean)) {
          if (hintTimer) { clearInterval(hintTimer); hintTimer = null; }
        }
      }, 1000);
    }

    // chat (SOS + skip now live inside createGameMenu)
    wireChatButton(container, 'loc-chat-btn', 'loc-chat-badge');

    // ОТВЕТИТЬ -> input phase
    container.querySelector('#loc-answer-btn')!.addEventListener('click', () => {
      phase = 'input';
      render();
    });
  }

  // ===================================================================
  //  INPUT PHASE (coordinates or address)
  // ===================================================================
  function renderInputPhase(): void {
    container.innerHTML = `
      <div class="screen screen-enter screen-split">
        <div class="screen-content">
          <div style="display: flex; align-items: center; gap: var(--sp-sm); margin-bottom: var(--sp-md);">
            <div style="flex: 1 1 0;"></div>
            <div id="loc-timer" style="flex: 0 0 auto;"></div>
            <div style="flex: 1 1 0; display: flex; justify-content: flex-end; align-items: center; gap: var(--sp-sm);">
              <div id="loc-menu"></div>
            </div>
          </div>

          <!-- Media goes FIRST — after session 43 merged the separate riddle
               screen into this input phase, the renderMedia call from the
               now-dead riddle phase was orphaned; players never saw audio /
               image / video attachments. -->
          ${renderMedia(loc.mediaUrl, loc.mediaType, !!riddleText, loc.additionalMedia)}

          ${riddleText ? `<div style="margin-bottom: var(--sp-md); padding: var(--sp-md); background: var(--bg-surface); border-radius: var(--radius-lg); border-left: 3px solid var(--amber);">
            <p class="text-eyebrow" style="margin-bottom: var(--sp-xs);">ЗАГАДКА</p>
            <p class="text-body" style="white-space: pre-wrap;">${escapeHtml(riddleText)}</p>
          </div>` : ''}

          <!-- Hints live under the riddle (session 47e). -->
          <div id="loc-hint-countdown" class="text-body" style="color: var(--text-hint); text-align: center; margin-bottom: var(--sp-md); display: none;">
            Подсказка через <span id="hint-seconds"></span>
          </div>
          <div id="loc-hints-container" style="margin-bottom: var(--sp-sm);"></div>

          <div class="spacer"></div>
        </div>

        <div class="screen-controls">
          <section aria-labelledby="loc-answer-label" class="screen-controls-form" style="margin-top: var(--sp-xl); padding-top: var(--sp-lg); border-top: 1px solid var(--bg-border);">
            <p id="loc-answer-label" class="text-eyebrow" style="margin-bottom: var(--sp-md);">ОТВЕТ</p>

            <div id="geo-coords" style="display: ${activeTab === 'coords' ? 'flex' : 'none'}; flex-direction: column; gap: var(--sp-md);">
              <div>
                <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">ШИРОТА</label>
                <input id="geo-lat" class="input" type="text" inputmode="decimal" placeholder="56.1445" data-testid="geo-lat" />
              </div>
              <div>
                <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">ДОЛГОТА</label>
                <input id="geo-lon" class="input" type="text" inputmode="decimal" placeholder="47.2511" data-testid="geo-lon" />
              </div>
            </div>

            <div id="geo-address" style="display: ${activeTab === 'address' ? 'flex' : 'none'}; flex-direction: column; gap: var(--sp-md);">
              <div style="position: relative;">
                <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">ГОРОД</label>
                <input id="geo-city" class="input" type="text" placeholder="Начните вводить..." autocomplete="off" />
                <div id="geo-city-suggest" class="suggest-list"></div>
              </div>
              <div style="position: relative;">
                <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">УЛИЦА</label>
                <input id="geo-street" class="input" type="text" placeholder="Начните вводить..." autocomplete="off" />
                <div id="geo-street-suggest" class="suggest-list"></div>
              </div>
              <div style="position: relative;">
                <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">ДОМ</label>
                <input id="geo-house" class="input" type="text" placeholder="Номер" autocomplete="off" />
                <div id="geo-house-suggest" class="suggest-list"></div>
              </div>
            </div>

            <div id="geo-error" class="text-body" style="color: var(--danger); display: none; text-align: center; margin-top: var(--sp-md);"></div>
          </section>

          <div style="display: flex; flex-direction: column; gap: var(--sp-md); margin-top: var(--sp-lg);">
            <button id="geo-submit" class="btn btn-primary btn-full" data-testid="geo-submit">ПРОВЕРИТЬ</button>
            ${state.canChat ? `<button id="loc-chat-btn" class="btn btn-secondary btn-full" style="position: relative;">
              Чат
              <span id="loc-chat-badge" class="badge" style="position: absolute; top: -8px; right: -8px; display: none;"></span>
            </button>` : ''}
          </div>
        </div>
      </div>
      <style>
        .suggest-list { max-height: 200px; overflow-y: auto; background: var(--bg-surface); border-radius: var(--radius-lg); margin-top: 2px; }
        .suggest-list:empty { display: none; }
        .suggest-item { padding: 12px 16px; cursor: pointer; color: var(--text-primary); font-size: 14px; border-bottom: 1px solid var(--bg-border); }
        .suggest-item:last-child { border-bottom: none; }
        .suggest-item:active { background: var(--bg-card); }
        .suggest-item:focus { background: var(--bg-card); color: var(--amber); outline: none; }
        .input:disabled { opacity: 0.5; }
      </style>
    `;

    // Timer + menu
    timerHandle = createTimer(container.querySelector('#loc-timer')!, loc.timeLimitSec, () => {
      triggerAdvance('TIMEOUT', activeLc!);
    });
    menuHandle = createGameMenu(container.querySelector('#loc-menu')!, activeLc!, { allowSkip: true });

    // Hints (visible during input phase too!)
    renderHintsIntoContainer();
    renderHintCountdown();

    // Hint countdown + reveal interval (same as riddle phase)
    if (decryptedHints.length > 0 && !revealedHints.every(Boolean)) {
      if (hintTimer) { clearInterval(hintTimer); hintTimer = null; }
      hintTimer = setInterval(() => {
        const before = revealedHints.filter(Boolean).length;
        updateHintReveals();
        const after = revealedHints.filter(Boolean).length;
        if (after > before) {
          renderHintsIntoContainer();
        }
        renderHintCountdown();
        if (revealedHints.every(Boolean)) {
          if (hintTimer) { clearInterval(hintTimer); hintTimer = null; }
        }
      }, 1000);
    }

    // chat (SOS + skip now live inside createGameMenu)
    wireChatButton(container, 'loc-chat-btn', 'loc-chat-badge');

    // Prime the browser's geolocation cache on mount — fire and forget.
    // When the player later clicks «ПРОВЕРИТЬ», the second capture call
    // inside the submit handler hits `maximumAge:30s` and returns almost
    // instantly instead of waiting up to 1.2s for a fresh GPS fix.
    void capturePlayerPosition();

    // (riddle phase removed — input is now the initial phase)

    // --- FIAS autocomplete cascade ---
    const latInput = container.querySelector<HTMLInputElement>('#geo-lat')!;
    const lonInput = container.querySelector<HTMLInputElement>('#geo-lon')!;
    const cityInput = container.querySelector<HTMLInputElement>('#geo-city')!;
    const streetInput = container.querySelector<HTMLInputElement>('#geo-street')!;
    const houseInput = container.querySelector<HTMLInputElement>('#geo-house')!;
    const citySuggest = container.querySelector<HTMLElement>('#geo-city-suggest')!;
    const streetSuggest = container.querySelector<HTMLElement>('#geo-street-suggest')!;
    const houseSuggest = container.querySelector<HTMLElement>('#geo-house-suggest')!;

    // Draft restore + live-save. A round-trip to chat (or any view swap)
    // destroys the DOM — without this the player has to re-type everything.
    const inputDraft = getDraft(state.currentLocationIndex);
    if (inputDraft.lat) latInput.value = inputDraft.lat;
    if (inputDraft.lon) lonInput.value = inputDraft.lon;
    if (inputDraft.city) cityInput.value = inputDraft.city;
    if (inputDraft.street) streetInput.value = inputDraft.street;
    if (inputDraft.house) houseInput.value = inputDraft.house;
    latInput.addEventListener('input', () => { inputDraft.lat = latInput.value; });
    lonInput.addEventListener('input', () => { inputDraft.lon = lonInput.value; });
    cityInput.addEventListener('input', () => { inputDraft.city = cityInput.value; });
    streetInput.addEventListener('input', () => { inputDraft.street = streetInput.value; });
    houseInput.addEventListener('input', () => { inputDraft.house = houseInput.value; });

    let cityTimer2: ReturnType<typeof setTimeout> | null = null;
    let streetTimer2: ReturnType<typeof setTimeout> | null = null;
    let houseTimer2: ReturnType<typeof setTimeout> | null = null;

    let cityItems: AddressSuggestion[] = [];
    let streetItems: AddressSuggestion[] = [];
    let houseItems: AddressSuggestion[] = [];

    function renderSuggestions(
      listEl: HTMLElement,
      items: AddressSuggestion[],
      onSelect: (item: AddressSuggestion) => void,
      ownerInput: HTMLInputElement,
    ): void {
      if (items.length === 0) { listEl.innerHTML = ''; return; }
      listEl.innerHTML = items.map(item =>
        `<div class="suggest-item" tabindex="0" data-id="${item.objectId}">${escapeHtml(item.name)}</div>`
      ).join('');
      const itemEls = Array.from(listEl.querySelectorAll<HTMLElement>('.suggest-item'));
      itemEls.forEach((el, i) => {
        el.addEventListener('mousedown', (ev) => ev.preventDefault());
        el.addEventListener('click', () => onSelect(items[i]));
        el.addEventListener('keydown', (ev) => {
          const ke = ev as KeyboardEvent;
          if (ke.key === 'ArrowDown') {
            ke.preventDefault();
            const target = nextIndex(i, itemEls.length, 1);
            if (target >= 0) itemEls[target].focus();
          } else if (ke.key === 'ArrowUp') {
            ke.preventDefault();
            const target = nextIndex(i, itemEls.length, -1);
            if (target >= 0) itemEls[target].focus();
          } else if (ke.key === 'Enter' || ke.key === ' ') {
            ke.preventDefault();
            onSelect(items[i]);
            ownerInput.focus();
          } else if (ke.key === 'Escape') {
            ke.preventDefault();
            listEl.innerHTML = '';
            ownerInput.focus();
          } else if (ke.key === 'Tab') {
            listEl.innerHTML = '';
          }
        });
      });
    }

    function handleInputArrowDown(ev: KeyboardEvent, listEl: HTMLElement): void {
      if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
      const itemEls = Array.from(listEl.querySelectorAll<HTMLElement>('.suggest-item'));
      if (itemEls.length === 0) return;
      ev.preventDefault();
      const target = nextIndex(-1, itemEls.length, ev.key === 'ArrowDown' ? 1 : -1);
      if (target >= 0) itemEls[target].focus();
    }

    function selectCity(item: AddressSuggestion): void {
      selectedCityId = item.objectId;
      cityInput.value = item.name;
      cityInput.style.color = '';
      citySuggest.innerHTML = '';
      selectedStreetId = null;
      selectedBuildingId = null;
    }

    function selectStreet(item: AddressSuggestion): void {
      selectedStreetId = item.objectId;
      streetInput.value = item.name;
      streetInput.style.color = '';
      streetSuggest.innerHTML = '';
      selectedBuildingId = null;
    }

    function selectHouse(item: AddressSuggestion): void {
      selectedBuildingId = item.objectId;
      houseInput.value = item.name;
      houseInput.style.color = '';
      houseSuggest.innerHTML = '';
    }

    // City input
    cityInput.addEventListener('input', () => {
      if (cityTimer2) clearTimeout(cityTimer2);
      selectedCityId = null;
      selectedStreetId = null;
      selectedBuildingId = null;
      cityInput.style.color = 'var(--amber)';
      streetItems = [];
      houseItems = [];
      streetSuggest.innerHTML = '';
      houseSuggest.innerHTML = '';

      const q = cityInput.value.trim();
      if (q.length < 2) { cityItems = []; citySuggest.innerHTML = ''; return; }

      cityTimer2 = setTimeout(async () => {
        try {
          cityItems = await searchCities(q);
          renderSuggestions(citySuggest, cityItems, selectCity, cityInput);
        } catch { cityItems = []; citySuggest.innerHTML = ''; }
      }, 300);
    });

    streetInput.addEventListener('input', () => {
      if (streetTimer2) clearTimeout(streetTimer2);
      selectedStreetId = null;
      selectedBuildingId = null;
      streetInput.style.color = 'var(--amber)';
      houseItems = [];
      houseSuggest.innerHTML = '';

      const q = streetInput.value.trim();
      if (q.length < 2 || !selectedCityId) { streetItems = []; streetSuggest.innerHTML = ''; return; }

      streetTimer2 = setTimeout(async () => {
        try {
          streetItems = await searchStreets(selectedCityId!, q);
          renderSuggestions(streetSuggest, streetItems, selectStreet, streetInput);
        } catch { streetItems = []; streetSuggest.innerHTML = ''; }
      }, 300);
    });

    houseInput.addEventListener('input', () => {
      if (houseTimer2) clearTimeout(houseTimer2);
      selectedBuildingId = null;
      houseInput.style.color = 'var(--amber)';

      const q = houseInput.value.trim();
      if (!q || !selectedStreetId) { houseItems = []; houseSuggest.innerHTML = ''; return; }

      houseTimer2 = setTimeout(async () => {
        try {
          houseItems = await searchBuildings(selectedStreetId!, q);
          renderSuggestions(houseSuggest, houseItems, selectHouse, houseInput);
        } catch { houseItems = []; houseSuggest.innerHTML = ''; }
      }, 300);
    });

    // Auto-select on blur
    cityInput.addEventListener('blur', () => {
      setTimeout(() => {
        if (citySuggest.contains(document.activeElement)) return;
        const res = tryAutoSelect(cityInput.value, cityItems, selectedCityId != null);
        if (res.selected) selectCity(res.selected);
        citySuggest.innerHTML = '';
      }, 150);
    });
    streetInput.addEventListener('blur', () => {
      setTimeout(() => {
        if (streetSuggest.contains(document.activeElement)) return;
        const res = tryAutoSelect(streetInput.value, streetItems, selectedStreetId != null);
        if (res.selected) selectStreet(res.selected);
        streetSuggest.innerHTML = '';
      }, 150);
    });
    houseInput.addEventListener('blur', () => {
      setTimeout(() => {
        if (houseSuggest.contains(document.activeElement)) return;
        const res = tryAutoSelect(houseInput.value, houseItems, selectedBuildingId != null);
        if (res.selected) selectHouse(res.selected);
        houseSuggest.innerHTML = '';
      }, 150);
    });

    // Keydown: ArrowDown/ArrowUp + Tab auto-select
    cityInput.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'ArrowDown' || ke.key === 'ArrowUp') { handleInputArrowDown(ke, citySuggest); return; }
      if (ke.key !== 'Tab') return;
      const res = tryAutoSelect(cityInput.value, cityItems, selectedCityId != null);
      if (res.selected) selectCity(res.selected);
    });
    streetInput.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'ArrowDown' || ke.key === 'ArrowUp') { handleInputArrowDown(ke, streetSuggest); return; }
      if (ke.key !== 'Tab') return;
      const res = tryAutoSelect(streetInput.value, streetItems, selectedStreetId != null);
      if (res.selected) selectStreet(res.selected);
    });
    houseInput.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'ArrowDown' || ke.key === 'ArrowUp') { handleInputArrowDown(ke, houseSuggest); return; }
      if (ke.key !== 'Tab') return;
      const res = tryAutoSelect(houseInput.value, houseItems, selectedBuildingId != null);
      if (res.selected) selectHouse(res.selected);
    });

    // Close suggestions on outside click
    container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#geo-city') && !target.closest('#geo-city-suggest')) citySuggest.innerHTML = '';
      if (!target.closest('#geo-street') && !target.closest('#geo-street-suggest')) streetSuggest.innerHTML = '';
      if (!target.closest('#geo-house') && !target.closest('#geo-house-suggest')) houseSuggest.innerHTML = '';
    });

    // Submit geo answer
    container.querySelector('#geo-submit')!.addEventListener('click', async () => {
      const btn = container.querySelector<HTMLButtonElement>('#geo-submit')!;
      btn.disabled = true;
      btn.textContent = 'Проверка...';
      hideError();

      // Capture player's actual GPS position in parallel with the verify
      // flow. Attach to the payload when it resolves — never blocks submit.
      const playerPos = await capturePlayerPosition();

      try {
        const currentKey = state.currentLocationIndex === 0
          ? keyFromBase64(state.firstRiddleKey)
          : keyFromBase64(state.nextKeys[state.currentLocationIndex - 1]);

        if (activeTab === 'coords') {
          // /investigate 2026-04-27 user report — iPhone/Android decimal
          // keyboards in ru-RU locale produce a COMMA as the decimal
          // separator (matching browser system locale). `parseFloat("55,12")`
          // returns 55, dropping everything past the comma → silently wrong
          // coords pass the isNaN check and the user fails verification with
          // no idea why. Normalize to dot before parsing. The Locations.answerLat/Lon
          // pin is in degrees so no thousands separator collision.
          const latRaw = (container.querySelector('#geo-lat') as HTMLInputElement).value.replace(',', '.');
          const lonRaw = (container.querySelector('#geo-lon') as HTMLInputElement).value.replace(',', '.');
          const lat = parseFloat(latRaw);
          const lon = parseFloat(lonRaw);
          if (isNaN(lat) || isNaN(lon)) { showError('Введите координаты'); btn.disabled = false; btn.textContent = 'ПРОВЕРИТЬ'; return; }

          let res: { correct: boolean; message: string; targetLat?: number; targetLon?: number };
          if (state.fullOffline && loc.encryptedAnswerData) {
            res = await verifyGeoLocal(loc.encryptedAnswerData, currentKey, 'COORDINATES', loc.geofenceRadiusM || 50, { lat, lon });
          } else {
            res = await verifyGeoAnswer(state.gameCode, {
              playerId: state.playerId, locationId: loc.id, answerType: 'COORDINATES', lat, lon,
              playerLat: playerPos?.lat, playerLon: playerPos?.lon,
            });
          }

          if (res.correct) {
            lastAnswerLat = res.targetLat ?? lat;
            lastAnswerLon = res.targetLon ?? lon;
            setState({ geoVerified: { lat: lastAnswerLat!, lon: lastAnswerLon! } });
            phase = 'site-code';
            render();
          } else {
            showError(res.message || 'Неверные координаты');
            btn.disabled = false;
            btn.textContent = 'ПРОВЕРИТЬ';
          }
        } else {
          // Address — flush pending auto-selects
          if (!selectedCityId) {
            const res = tryAutoSelect(cityInput.value, cityItems, false);
            if (res.selected) selectCity(res.selected);
          }
          if (!selectedStreetId) {
            const res = tryAutoSelect(streetInput.value, streetItems, false);
            if (res.selected) selectStreet(res.selected);
          }
          if (!selectedBuildingId) {
            const res = tryAutoSelect(houseInput.value, houseItems, false);
            if (res.selected) selectHouse(res.selected);
          }

          const cityText = cityInput.value.trim();
          const streetText = streetInput.value.trim();
          const houseText = houseInput.value.trim();

          if (!cityText || !streetText || !houseText) {
            showError('Введите город, улицу и дом');
            btn.disabled = false;
            btn.textContent = 'ПРОВЕРИТЬ';
            return;
          }

          const allIdsSelected = selectedCityId && selectedStreetId && selectedBuildingId;

          let res: { correct: boolean; message: string; targetLat?: number; targetLon?: number };
          if (state.fullOffline && loc.encryptedAnswerData) {
            if (!allIdsSelected) {
              showError('В оффлайн-режиме выберите адрес из подсказок');
              btn.disabled = false;
              btn.textContent = 'ПРОВЕРИТЬ';
              return;
            }
            res = await verifyGeoLocal(loc.encryptedAnswerData, currentKey, 'ADDRESS', 0, {
              cityObjectId: selectedCityId!, streetObjectId: selectedStreetId!, buildingObjectId: selectedBuildingId!,
            });
          } else {
            res = await verifyGeoAnswer(state.gameCode, {
              playerId: state.playerId, locationId: loc.id, answerType: 'ADDRESS',
              cityObjectId: selectedCityId ?? undefined,
              streetObjectId: selectedStreetId ?? undefined,
              buildingObjectId: selectedBuildingId ?? undefined,
              cityText, streetText, houseText,
              playerLat: playerPos?.lat, playerLon: playerPos?.lon,
            });
          }

          if (res.correct) {
            lastAnswerAddress = `${cityText}, ${streetText} ${houseText}`.trim();
            if (res.targetLat != null && res.targetLon != null) {
              lastAnswerLat = res.targetLat;
              lastAnswerLon = res.targetLon;
            }
            // /investigate 2026-04-27 follow-up — when targetLat/Lon come
            // back null (org typed address with no FIAS pin → no geocode),
            // the pre-fix `?? 0` fallback wrote `lat:0, lon:0` to the store.
            // renderSiteCodePhase then drew «0.000000, 0.000000» (Гвинейский
            // залив) and «Построить маршрут» pointed there. Address-only
            // verification is a real outcome: store null coords + the
            // address; downstream consumers already null-tolerant.
            setState({ geoVerified: { lat: lastAnswerLat, lon: lastAnswerLon, address: lastAnswerAddress } });
            phase = 'site-code';
            render();
          } else {
            showError(res.message || 'Неверный адрес');
            btn.disabled = false;
            btn.textContent = 'ПРОВЕРИТЬ';
          }
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Ошибка сети');
        btn.disabled = false;
        btn.textContent = 'ПРОВЕРИТЬ';
      }
    });
  }

  // ===================================================================
  //  SITE-CODE PHASE
  // ===================================================================
  function renderSiteCodePhase(): void {
    // /investigate 2026-04-27 (iPhone testing) — defensive single-source-of-
    // truth refresh. Pre-fix, module-locals `lastAnswerLat/Lon/Address` were
    // updated by gated assignments in the WS GEO_VERIFIED handler and the
    // submitter path. When a stage's `Locations.answerLat/Lon` were null
    // (ADDRESS-type without geocode) the gate skipped, leaving module-locals
    // either at closure-init null OR — in pathological scenarios — at a
    // previous stage's values. The site-code card then rendered «Построить
    // маршрут» pointing at the PREVIOUS riddle's coords. Fix: read from
    // `state.geoVerified` (always current-stage-scoped, cleared on advance)
    // on every site-code render. Module-locals stay as a render-input cache
    // but become DERIVED state — same value as state.geoVerified, never
    // a stale snapshot. Same class as session-50 post-ship Bug 3 («не
    // показывался Построить маршрут»), different trigger.
    const v = getState().geoVerified;
    if (v) {
      lastAnswerLat = typeof v.lat === 'number' ? v.lat : null;
      lastAnswerLon = typeof v.lon === 'number' ? v.lon : null;
      lastAnswerAddress = v.address ?? null;
    } else {
      lastAnswerLat = null;
      lastAnswerLon = null;
      lastAnswerAddress = null;
    }

    const effectiveHashes = loc.siteCodeHash ? loc.siteCodeHash.split('|').filter(Boolean) : [];
    const codeCount = loc.siteCodeCount || effectiveHashes.length;

    // No site codes configured — skip phase, auto-submit empty answer
    if (codeCount === 0) {
      container.innerHTML = `
        <div class="screen screen-enter">
          <div id="loc-timer" style="margin-bottom: var(--sp-lg);"></div>
          <div class="card" style="margin-bottom: var(--sp-lg);">
            <p class="text-eyebrow" style="margin-bottom: var(--sp-sm); text-align: center;">МЕСТО НАЙДЕНО</p>
            ${lastAnswerLat != null && lastAnswerLon != null
              ? `<p class="text-data" style="text-align: center; margin-bottom: var(--sp-sm); font-family: var(--font-mono);">${lastAnswerLat.toFixed(6)}, ${lastAnswerLon.toFixed(6)}</p>`
              : ''}
            ${lastAnswerAddress
              ? `<p class="text-body" style="text-align: center; margin-bottom: var(--sp-sm);">${escapeHtml(lastAnswerAddress)}</p>`
              : ''}
          </div>
          <div id="site-overlay" class="overlay" style="display:none;"><div class="overlay-content"><div class="overlay-icon"></div><div class="overlay-title"></div><div class="overlay-text"></div></div></div>
        </div>`;
      timerHandle = createTimer(container.querySelector('#loc-timer')!, loc.timeLimitSec, () => {
        triggerAdvance('TIMEOUT', activeLc!);
      });

      // Auto-submit after short delay so player sees "МЕСТО НАЙДЕНО"
      setTimeout(async () => {
        const state = getState();
        const overlay = container.querySelector<HTMLElement>('#site-overlay')!;
        const playerPos = await capturePlayerPosition();
        try {
          let nextRiddleKey: string | undefined;
          let nextEncryptedRiddle: string | undefined;
          let nextEncryptedHint: string | undefined;
          let nextEncryptedHints: Array<{encryptedText: string; delaySec: number; orderIndex: number}> = [];
          // /autoplan 2026-04-24 — capture server anchor from submitAnswer so
          // the advance setState below can seed from the fresh Teams.locationEnteredAt
          // instead of raw Date.now(). Null on offline branch (no server call).
          let advanceAnchor: number | null = null;
          let advanceServerNow: number | null = null;

          if (state.fullOffline) {
            const answerHash = loc.answerHash || '';
            if (answerHash) {
              const nextKeyBytes = await deriveKey(answerHash);
              nextRiddleKey = btoa(String.fromCharCode(...nextKeyBytes));
            }
          } else {
            const res = await submitAnswer(state.gameCode, {
              playerId: state.playerId, locationId: loc.id, answer: '',
              playerLat: playerPos?.lat, playerLon: playerPos?.lon,
            });
            nextRiddleKey = res.nextRiddleKey;
            nextEncryptedRiddle = res.nextEncryptedRiddle;
            nextEncryptedHint = res.nextEncryptedHint;
            nextEncryptedHints = res.nextEncryptedHints || [];
            advanceAnchor = res.locationEnteredAt ?? null;
            advanceServerNow = res.serverNow ?? null;
          }

          if (nextRiddleKey) {
            const keys = [...state.nextKeys];
            keys[state.currentLocationIndex] = nextRiddleKey;
            setState({ nextKeys: keys });
          }
          if (nextEncryptedRiddle) {
            const nextIdx = state.currentLocationIndex + 1;
            if (nextIdx < state.locations.length) {
              const updatedLocations = [...state.locations];
              updatedLocations[nextIdx] = {
                ...updatedLocations[nextIdx],
                encryptedRiddle: nextEncryptedRiddle,
                encryptedHint: nextEncryptedHint || '',
                encryptedHints: nextEncryptedHints.length > 0 ? nextEncryptedHints : updatedLocations[nextIdx].encryptedHints,
              };
              setState({ locations: updatedLocations });
            }
          }

          showOverlay(overlay, true, 'ТОЧКА ПРОЙДЕНА!', '');
          const isLast = state.currentLocationIndex >= state.locations.length - 1;
          setTimeout(() => {
            if (isLast) {
              const now = Date.now();
              const current = getState();
              const total = (now - current.gameStartedAt) + current.penaltyMs;
              advanceLocationIndex(state.locations.length, { status: 'FINISHED', completedAt: now, totalTimeMs: total });
              navigate('results');
            } else {
              advanceLocationIndex(state.currentLocationIndex + 1, {
                locationStartedAt: anchorFromServer(advanceAnchor, advanceServerNow),
              });
              navigate('riddle');
            }
          }, 2000);
        } catch (err) {
          console.error('[location] auto-advance without site codes failed', err);
        }
      }, 1500);
      return;
    }

    // Session 53 — per-code required flags replace the old global/minRequired
    // pair. Flags[i]=true → input is required (HTML5 required attr + no
    // "необязательно" tag). Flags[i]=false → "необязательно" label and
    // blank input is accepted. If the wire didn't include flags (legacy
    // game), synthesize from siteCodeRequired (all-true / all-false).
    const legacyRequired = loc.siteCodeRequired !== false;
    const requiredFlags: boolean[] = loc.siteCodeRequiredFlags && loc.siteCodeRequiredFlags.length === codeCount
      ? loc.siteCodeRequiredFlags
      : Array.from({length: codeCount}, () => legacyRequired);
    const inputAttrs = loc.siteCodeDigitsOnly ? 'inputmode="numeric" pattern="[0-9]*"' : '';
    const placeholder = loc.siteCodeDigitsOnly ? 'Только цифры' : 'Введите код';

    const codeInputsHtml = Array.from({length: codeCount}, (_, i) => {
      const numSuffix = codeCount > 1 ? ` ${i + 1}` : '';
      const optionalTag = requiredFlags[i] ? '' : ' <span style="color:var(--text-hint);font-weight:400;">(необязательно)</span>';
      const requiredAttr = requiredFlags[i] ? 'required' : '';
      return `
      <div style="margin-bottom: var(--sp-sm);">
        <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">ВВЕДИТЕ КОД${numSuffix} С ЗАБРОШКИ${optionalTag}</label>
        <input class="input site-code-field" type="text" ${inputAttrs}
               placeholder="${placeholder}" autocomplete="off" data-idx="${i}" data-testid="site-code-${i}" ${requiredAttr} />
      </div>
    `;
    }).join('');

    container.innerHTML = `
      <div class="screen screen-enter screen-split">
        <div class="screen-content">
          <div style="display: flex; align-items: center; gap: var(--sp-sm); margin-bottom: var(--sp-md);">
            <div style="flex: 1 1 0;"></div>
            <div id="loc-timer" style="flex: 0 0 auto;"></div>
            <div style="flex: 1 1 0; display: flex; justify-content: flex-end; align-items: center; gap: var(--sp-sm);">
              <div id="loc-menu"></div>
            </div>
          </div>

          <div class="card" style="margin-bottom: var(--sp-lg);">
            <p class="text-eyebrow" style="margin-bottom: var(--sp-sm); text-align: center;">МЕСТО НАЙДЕНО</p>
            ${lastAnswerLat != null && lastAnswerLon != null
              ? `<p id="site-found-coords" class="text-data" style="text-align: center; margin-bottom: var(--sp-sm); font-family: var(--font-mono); user-select: all;">${lastAnswerLat.toFixed(6)}, ${lastAnswerLon.toFixed(6)}</p>`
              : ''}
            ${lastAnswerAddress
              ? `<p id="site-found-address" class="text-body" style="text-align: center; margin-bottom: var(--sp-sm); user-select: all;">${escapeHtml(lastAnswerAddress)}</p>`
              : ''}
            <div style="text-align: center;">
              <button id="site-map-btn" class="btn btn-secondary" style="min-height: 40px; padding: 8px 20px;" onclick="window.open(document.getElementById('site-map-btn').dataset.url, '_blank')">Построить маршрут</button>
            </div>
          </div>

          <div class="spacer"></div>
        </div>

        <div class="screen-controls">
          ${codeInputsHtml}
          ${loc.siteCodeDigitsOnly ? '<div class="text-body" style="color: var(--text-hint); font-size: 12px; margin-top: 4px;">В ответе только цифры</div>' : ''}

          <div id="site-error" class="text-body" style="color: var(--danger); display: none; text-align: center; margin-top: var(--sp-md);"></div>

          <div style="display: flex; flex-direction: column; gap: var(--sp-md); margin-top: var(--sp-lg);">
            <button id="site-submit" class="btn btn-primary btn-full" data-testid="site-submit">ПОДТВЕРДИТЬ</button>
            ${!requiredFlags.some(f => f) ? '<button id="site-skip" class="btn btn-full" style="background: transparent; border: 1px solid var(--bg-border); color: var(--text-secondary);">ПРОПУСТИТЬ</button>' : ''}
            ${state.canChat ? `<button id="loc-chat-btn" class="btn btn-secondary btn-full" style="position: relative;">
              Чат
              <span id="loc-chat-badge" class="badge" style="position: absolute; top: -8px; right: -8px; display: none;"></span>
            </button>` : ''}
          </div>
        </div>

        <div id="site-overlay" style="display: none; position: fixed; inset: 0; z-index: 100;
             justify-content: center; align-items: center; flex-direction: column; gap: var(--sp-lg);"></div>
      </div>
    `;

    // Timer + menu
    timerHandle = createTimer(container.querySelector('#loc-timer')!, loc.timeLimitSec, () => {
      triggerAdvance('TIMEOUT', activeLc!);
    });
    menuHandle = createGameMenu(container.querySelector('#loc-menu')!, activeLc!, { allowSkip: true });

    // chat (SOS + skip now live inside createGameMenu)
    wireChatButton(container, 'loc-chat-btn', 'loc-chat-badge');

    // Map URL
    const mapBtn = container.querySelector<HTMLElement>('#site-map-btn');
    if (mapBtn) {
      const mapUrl = buildYandexMapsUrl(lastAnswerLat, lastAnswerLon, lastAnswerAddress);
      if (mapUrl) {
        mapBtn.dataset.url = mapUrl;
      } else {
        mapBtn.style.display = 'none';
      }
    }

    // Tap-to-copy on "МЕСТО НАЙДЕНО" — lets the player paste coords /
    // address into Maps, messenger, etc. Disposers kept in a
    // module-level list and cleared on destroyLocationView.
    const coordsEl = container.querySelector<HTMLElement>('#site-found-coords');
    if (coordsEl && lastAnswerLat != null && lastAnswerLon != null) {
      copyDisposers.push(wireClickToCopy(coordsEl, {
        label: 'координаты',
        text: `${lastAnswerLat.toFixed(6)}, ${lastAnswerLon.toFixed(6)}`,
      }));
    }
    const addressEl = container.querySelector<HTMLElement>('#site-found-address');
    if (addressEl && lastAnswerAddress) {
      copyDisposers.push(wireClickToCopy(addressEl, {
        label: 'адрес',
        text: lastAnswerAddress,
      }));
    }

    const codeInputs = container.querySelectorAll<HTMLInputElement>('.site-code-field');
    const errorEl = container.querySelector<HTMLElement>('#site-error')!;
    const overlay = container.querySelector<HTMLElement>('#site-overlay')!;

    // Draft restore + live-save for site-code fields. Same motivation as
    // the input-phase drafts: a trip to chat must not wipe entered codes.
    const siteDraft = getDraft(state.currentLocationIndex);
    codeInputs.forEach((inp, i) => {
      const saved = siteDraft.siteCodes?.[i];
      if (saved) inp.value = saved;
      inp.addEventListener('input', () => {
        if (!siteDraft.siteCodes) siteDraft.siteCodes = [];
        siteDraft.siteCodes[i] = inp.value;
      });
    });

    async function doSubmit(answer: string): Promise<void> {
      // /investigate 2026-05-04: hard guard against re-entrance. The
      // auto-submit setTimeout and the manual click handler both reach
      // here; without this, duplicate Answer rows landed in the activity
      // feed (game 983541 «Городские тени» repro: two ✅ rows for «Ghost-42»
      // at 19:01:43). cancelAutoSubmit() also kills any pending auto-submit
      // so the manual click wins cleanly.
      if (submitInflight) return;
      submitInflight = true;
      cancelAutoSubmit();
      const btn = container.querySelector<HTMLButtonElement>('#site-submit')!;
      btn.disabled = true;
      btn.textContent = 'Проверка...';
      errorEl.style.display = 'none';

      const playerPos = await capturePlayerPosition();

      try {
        let correct = false;
        let message = '';
        let nextRiddleKey: string | undefined;
        let nextEncryptedRiddle: string | undefined;
        let nextEncryptedHint: string | undefined;
        let nextEncryptedHints: Array<{encryptedText: string; delaySec: number; orderIndex: number}> = [];
        // /autoplan 2026-04-24 — capture server anchor from submitAnswer so
        // the advance setState below seeds from Teams.locationEnteredAt via
        // anchorFromServer() instead of raw Date.now(). Null on offline
        // branch (no server call → helper falls back to Date.now()).
        let advanceAnchor: number | null = null;
        let advanceServerNow: number | null = null;

        if (state.fullOffline) {
          const codes = answer.split('|').map(c => c.trim()).filter(Boolean);
          const hashes = loc.siteCodeHash.split('|').filter(Boolean);
          const gameSalt = state.salt || '';

          // Session 53 — per-code required-flag verification, mirrors the
          // backend injective verifier (verifySiteCodesPerFlag). Each
          // submitted code consumes ONE unused hash; every required-flag
          // hash must get matched.
          const legacyReq = loc.siteCodeRequired !== false;
          const flags: boolean[] = loc.siteCodeRequiredFlags && loc.siteCodeRequiredFlags.length === hashes.length
            ? loc.siteCodeRequiredFlags
            : hashes.map(() => legacyReq);
          const anyRequired = flags.some(f => f);

          if (answer === '__SKIP__' && !anyRequired) {
            correct = true;
          } else {
            const remaining = hashes.map((h, i) => ({ index: i, hash: h }));
            const matched = new Set<number>();
            for (const code of codes) {
              const variants = [code.toLowerCase(), code];
              const hitPos = remaining.findIndex(r => variants.some(v => {
                try { return verifyBcrypt(v, gameSalt, r.hash); } catch { return false; }
              }));
              if (hitPos >= 0) {
                matched.add(remaining[hitPos].index);
                remaining.splice(hitPos, 1);
              }
            }
            correct = flags.every((req, i) => !req || matched.has(i));
          }

          if (correct) {
            const answerHash = loc.answerHash || '';
            if (answerHash) {
              const nextKeyBytes = await deriveKey(answerHash);
              nextRiddleKey = btoa(String.fromCharCode(...nextKeyBytes));
            }
            message = 'Верно!';
          } else {
            message = 'Неверный код, попробуйте ещё.';
          }
        } else {
          const res = await submitAnswer(state.gameCode, {
            playerId: state.playerId, locationId: loc.id, answer,
            playerLat: playerPos?.lat, playerLon: playerPos?.lon,
          });
          correct = res.correct;
          message = res.message;
          nextRiddleKey = res.nextRiddleKey;
          nextEncryptedRiddle = res.nextEncryptedRiddle;
          nextEncryptedHint = res.nextEncryptedHint;
          nextEncryptedHints = res.nextEncryptedHints || [];
          advanceAnchor = res.locationEnteredAt ?? null;
          advanceServerNow = res.serverNow ?? null;
        }

        if (correct) {
          if (nextRiddleKey) {
            const keys = [...state.nextKeys];
            keys[state.currentLocationIndex] = nextRiddleKey;
            setState({ nextKeys: keys });
          }
          if (nextEncryptedRiddle) {
            const nextIdx = state.currentLocationIndex + 1;
            if (nextIdx < state.locations.length) {
              const updatedLocations = [...state.locations];
              updatedLocations[nextIdx] = {
                ...updatedLocations[nextIdx],
                encryptedRiddle: nextEncryptedRiddle,
                encryptedHint: nextEncryptedHint || '',
                encryptedHints: nextEncryptedHints.length > 0 ? nextEncryptedHints : updatedLocations[nextIdx].encryptedHints,
              };
              setState({ locations: updatedLocations });
            }
          }

          showOverlay(overlay, true, 'ТОЧКА ПРОЙДЕНА!', message || '');

          // Location passed — input drafts are no longer relevant.
          clearDraft(state.currentLocationIndex);

          const isLast = state.currentLocationIndex >= state.locations.length - 1;
          setTimeout(() => {
            if (isLast) {
              const now = Date.now();
              const current = getState();
              const total = (now - current.gameStartedAt) + current.penaltyMs;
              advanceLocationIndex(state.locations.length, {
                status: 'FINISHED',
                completedAt: now,
                totalTimeMs: total,
              });
              navigate('results');
            } else {
              advanceLocationIndex(state.currentLocationIndex + 1, {
                locationStartedAt: anchorFromServer(advanceAnchor, advanceServerNow),
              });
              navigate('riddle');
            }
          }, 2000);
        } else {
          errorEl.textContent = message || 'Неверный код, попробуйте ещё.';
          errorEl.style.display = 'block';
          setState({ wrongAnswers: state.wrongAnswers + 1 });
          vibrateWrongAnswer();
          codeInputs.forEach(inp => inp.classList.add('shake'));
          setTimeout(() => {
            codeInputs.forEach(inp => inp.classList.remove('shake'));
          }, 600);
          // /investigate 2026-04-27 user report: «выйдет ошибка Неверный код
          // и оба поля снова обычного цвета». Pre-fix: unlockAllFields()
          // unconditionally — clobbered the locally-bcrypt-verified green
          // state of the CORRECT field, forcing the player to re-type a
          // code that already matched. Now: only unlock if auto-submit
          // fired (which means all required fields were locally green; if
          // server still rejected, it's a slot-consumption race or stale
          // server-side state and a full retry is the right recovery).
          // For a manual submit with mixed state, the wrong field is
          // already unlocked — the user just needs to fix that one.
          // Locked-green fields stay locked-green.
          if (autoSubmitted) {
            unlockAllFields();
          } else {
            // Reset only the auto-submit guard so a subsequent retype +
            // match can fire auto-submit again.
            autoSubmitted = false;
          }
          btn.disabled = false;
          btn.textContent = 'ПОДТВЕРДИТЬ';
          submitInflight = false;
        }
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : 'Ошибка сети';
        errorEl.style.display = 'block';
        // Same logic as the rejection branch — preserve green state for
        // manually-mixed submits, only unlock if everything was green.
        if (autoSubmitted) {
          unlockAllFields();
        } else {
          autoSubmitted = false;
        }
        btn.disabled = false;
        btn.textContent = 'ПОДТВЕРДИТЬ';
        submitInflight = false;
      }
    }

    // Per-field bcrypt validation. Injective — mirrors backend
    // verifySiteCodesPerFlag (session 50 codex#2): each locked field
    // consumes ONE unused hash slot, so duplicate codes for an [A,B]
    // location can't both go green.
    const hashes = loc.siteCodeHash ? loc.siteCodeHash.split('|') : [];
    const gameSaltForCheck = state.salt || '';
    const lockedFields = new Set<number>();
    const consumedHashSlots = new Set<number>();
    let autoSubmitted = false;
    // /investigate 2026-05-04 (game 983541 «Городские тени» — duplicate rows
    // in dashboard activity feed): all-fields-bcrypt-verified scheduled an
    // uncancellable setTimeout(doSubmit, 500) while the manual click handler
    // also unconditionally fired doSubmit. If user clicked submit within the
    // 500ms window (or the click handler's own checkField loop scheduled the
    // timer right before calling doSubmit inline), TWO submitAnswer POSTs
    // hit the server. The CAS in advanceTeam blocked double-advance, but the
    // Answers row insert at GamePlayOps.kt:96 happens BEFORE the CAS (late-
    // answer audit), so a second isCorrect=true row landed in the feed.
    // Fix: track the timer ID + add an in-flight guard inside doSubmit.
    let autoSubmitTimer: ReturnType<typeof setTimeout> | null = null;
    let submitInflight = false;

    function cancelAutoSubmit(): void {
      if (autoSubmitTimer != null) {
        clearTimeout(autoSubmitTimer);
        autoSubmitTimer = null;
      }
    }

    function unlockAllFields(): void {
      lockedFields.clear();
      consumedHashSlots.clear();
      autoSubmitted = false;
      cancelAutoSubmit();
      codeInputs.forEach(inp => {
        inp.disabled = false;
        inp.style.borderColor = '';
        inp.style.color = '';
        inp.style.opacity = '';
      });
      container.querySelectorAll('span[data-aq-check]').forEach(el => el.remove());
    }

    function checkField(inp: HTMLInputElement, idx: number): void {
      const code = inp.value.trim();
      if (!code || lockedFields.has(idx)) return;
      const variants = [code.toLowerCase(), code];
      let matchedHashIdx = -1;
      for (let h = 0; h < hashes.length; h++) {
        if (consumedHashSlots.has(h)) continue;
        const hit = variants.some(v => {
          try { return verifyBcrypt(v, gameSaltForCheck, hashes[h]); } catch { return false; }
        });
        if (hit) { matchedHashIdx = h; break; }
      }
      if (matchedHashIdx >= 0) {
        lockedFields.add(idx);
        consumedHashSlots.add(matchedHashIdx);
        inp.disabled = true;
        inp.style.borderColor = 'var(--success)';
        inp.style.color = 'var(--success)';
        inp.style.opacity = '0.8';
        const label = inp.parentElement?.querySelector('.text-eyebrow');
        if (label) label.innerHTML += ' <span data-aq-check style="color:var(--success);">✓</span>';

        // Session 53 — "all verified" means every REQUIRED flag has been
        // locked (bcrypt-verified). Optional fields don't gate auto-submit.
        const allVerified = requiredFlags.every(
          (req, i) => !req || lockedFields.has(i)
        );
        if (allVerified && !autoSubmitted) {
          autoSubmitted = true;
          const codes = Array.from(codeInputs).map(f => f.value.trim()).filter(Boolean);
          cancelAutoSubmit();
          autoSubmitTimer = setTimeout(() => {
            autoSubmitTimer = null;
            doSubmit(codes.join('|'));
          }, 500);
        }
      }
    }

    codeInputs.forEach((inp, idx) => {
      inp.addEventListener('blur', () => checkField(inp, idx));
      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); checkField(inp, idx); }
      });
      inp.addEventListener('input', () => {
        if (errorEl.style.display !== 'none') errorEl.style.display = 'none';
      });
    });

    container.querySelector('#site-submit')!.addEventListener('click', () => {
      codeInputs.forEach((inp, idx) => checkField(inp, idx));
      const codes = Array.from(codeInputs).map(inp => inp.value.trim());
      // Session 53 — gate on per-flag required. Any empty REQUIRED input
      // focuses that input instead of submitting. Optional inputs may
      // legitimately be empty.
      for (let i = 0; i < requiredFlags.length; i++) {
        if (requiredFlags[i] && !codes[i]) { codeInputs[i].focus(); return; }
      }
      const filled = codes.filter(Boolean);
      if (filled.length === 0) return;
      doSubmit(filled.join('|'));
    });

    const siteSkipBtn = container.querySelector('#site-skip');
    if (siteSkipBtn) {
      siteSkipBtn.addEventListener('click', () => {
        doSubmit('__SKIP__');
      });
    }
  }

  // ===================================================================
  //  SHARED HELPERS
  // ===================================================================

  function showOverlay(overlayEl: HTMLElement, success: boolean, title: string, message: string): void {
    overlayEl.style.display = 'flex';
    overlayEl.style.background = success ? 'rgba(0, 230, 118, 0.95)' : 'rgba(255, 82, 82, 0.95)';
    overlayEl.className = 'overlay-enter';
    overlayEl.innerHTML = `
      <div style="font-size: 64px;">${success ? '✓' : '✗'}</div>
      <h2 class="text-display-large" style="color: white;">${title}</h2>
      <p class="text-body-large" style="color: white;">${escapeHtml(message)}</p>
    `;
  }

  function showError(msg: string): void {
    const el = container.querySelector<HTMLElement>('#geo-error, #site-error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  function hideError(): void {
    const el = container.querySelector<HTMLElement>('#geo-error, #site-error');
    if (el) el.style.display = 'none';
  }
}
